import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { BackButton } from '@/components/ui/back-button';
import { EmbeddedInfiniteScrollList } from '@/components/ui/embedded-infinite-scroll-list';
import { OmnisearchSelect } from '@/components/ui/omnisearch-select';
import { StatusBadge } from '@/components/ui/status-badge';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    ProcessorEditor,
    buildProcessorConfig,
    buildProcessorCreateRequest,
    createEmptyProcessorEditorValue,
    createProcessorEditorValueFromResponse,
    type ProcessorEditorValue,
    validateProcessorEditorValue,
} from '@/components/payments-infrastructure/ProcessorEditor';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { SecretModal } from '@/components/secrets/SecretModal';
import { SecretActionButtons } from '@/components/secrets/SecretActionButtons';
import { CredentialCardView } from '@/components/secrets/CredentialCardView';
import { CredentialDeleteDialog } from '@/components/secrets/CredentialDeleteDialog';
import { CredentialDisconnectDialog } from '@/components/secrets/CredentialDisconnectDialog';
import {
    ApiError,
    authApi,
    organizationApi,
    paymentsInfrastructureApi,
    secretsApi,
} from '@/lib/pillarApi';
import {
    useCredentialUsages,
    useDeleteSecret,
    useDisableSecret,
    useDisconnectCredentialUsages,
    useEnableSecret,
} from '@/lib/hooks/useSecrets';
import {
    buildCredentialUsageSelectionKey,
    toCredentialUsageReference,
} from '@/lib/secrets/credentialUsageSelection';
import { parseCredentialDeleteConflictUsages } from '@/lib/secrets/credentialDeleteConflict';
import {
    getLogicalTerminalDisplayLabel,
    getTerminalDeviceDisplayLabel,
} from '@/lib/payments-infrastructure/displayLabels';
import { runInBackground } from '@/lib/utils';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import { useUnsavedChangesRegistration } from '@/lib/hooks/useUnsavedChangesGuard';
import type {
    ActiveRoleAssignmentResponse,
    CredentialType,
    CreateLogicalTerminalRequest,
    CreateMerchantAccountRequest,
    CreateProcessorRequest,
    GetOrganizationResponse,
    CreateTerminalDeviceRequest,
    GetLogicalTerminalResponse,
    GetMerchantAccountResponse,
    GetProcessorResponse,
    GetTerminalDeviceResponse,
    LogicalTerminalConfiguration,
    LogicalTerminalListItem,
    MerchantAccountConfiguration,
    MerchantAccountSearchListItem,
    ProcessorListItem,
    OrganizationListItem,
    OrganizationOmnisearchListItem,
    CreateSecretResponse,
    CredentialUsageListItem,
    CredentialUsageReference,
    LogicalTerminalOrganizationConflictItem,
    MerchantOrganizationListItem,
    SecretIdentityReference,
    SecretListItem,
    SupportedCredentialSystemId,
    TerminalDeviceConfiguration,
    TerminalDeviceListItem,
} from '@pillar-portal/api-types';
import {
    CredentialTypeDisplayName,
    IpgSystemId,
    IpgSystemIdToName,
    SupportedIpgSystemIds,
    ThirdPartySystemIdToName,
    hasInternalAdminPrivileges,
    hasPaymentsInfrastructurePage,
} from '@pillar-portal/api-types';
import { Status } from '@pillar-portal/database/dist/frontend';
import type { CredentialTargetFamily } from '@/components/secrets/SecretsCard';

function useActiveRoleAssignmentState(): {
    activeRoleAssignment: ActiveRoleAssignmentResponse | null | undefined;
    isLoading: boolean;
} {
    const activeRoleAssignmentQuery = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await authApi.getActiveRoleAssignment();
        },
        staleTime: 30000,
    });
    return {
        activeRoleAssignment: activeRoleAssignmentQuery.data,
        isLoading: activeRoleAssignmentQuery.isLoading,
    };
}

const OpaqueEmbeddedListCursorSchema = z.string().trim().min(1);
const MerchantAccountUuidSearchParamSchema = z.string().uuid();
const LogicalTerminalUuidSearchParamSchema = z.string().uuid();
export const OrganizationUuidSearchParamSchema: z.ZodString = z.string().uuid();

function parseOpaqueEmbeddedListCursorParam(pageParam: unknown): string | undefined {
    if (pageParam === null || pageParam === undefined) {
        return undefined;
    }
    return OpaqueEmbeddedListCursorSchema.parse(pageParam);
}

function parseLockedOrganizationUuidSearchParam(
    searchParams: URLSearchParams
): string | null {
    const organizationUuidParam: string | null = searchParams.get('organization_uuid');
    if (organizationUuidParam === null) {
        return null;
    }
    return OrganizationUuidSearchParamSchema.parse(organizationUuidParam);
}

function toOptionalNumber(value: string): number | undefined {
    const normalizedValue: string = value.trim();
    if (normalizedValue.length === 0) {
        return undefined;
    }
    return Number(normalizedValue);
}

function toEditableNumberInputValue(value: number | undefined): string {
    return value === undefined ? '' : String(value);
}

function isEmptyTerminalDeviceConfiguration(
    configuration: TerminalDeviceConfiguration | undefined
): boolean {
    if (configuration === undefined) {
        return true;
    }
    return Object.values(configuration).every((value) => value === undefined);
}

function selectActiveProcessors(
    processors: ReadonlyArray<ProcessorListItem>
): ReadonlyArray<ProcessorListItem> {
    return processors.filter((processor) => processor.status === Status.ACTIVE);
}

function nextInheritedCheckboxValue(value: boolean | undefined): boolean | undefined {
    if (value === undefined) {
        return true;
    }
    if (value === true) {
        return false;
    }
    return undefined;
}

function InheritedCheckbox(props: {
    id: string;
    value: boolean | undefined;
    disabled?: boolean;
    onValueChange: (value: boolean | undefined) => void;
    label: string;
}): ReactElement {
    const checkboxRef = useRef<HTMLInputElement | null>(null);
    const visualState: 'checked' | 'unchecked' | 'mixed' =
        props.value === undefined ? 'mixed' : props.value ? 'checked' : 'unchecked';

    useEffect(() => {
        if (checkboxRef.current !== null) {
            checkboxRef.current.indeterminate = props.value === undefined;
        }
    }, [props.value]);

    return (
        <div className="flex items-center gap-2">
            <Checkbox
                id={props.id}
                ref={checkboxRef}
                checked={props.value === true}
                disabled={props.disabled}
                aria-checked={props.value === undefined ? 'mixed' : props.value}
                visualState={visualState}
                onClick={(event) => {
                    event.preventDefault();
                    if (props.disabled) {
                        return;
                    }
                    props.onValueChange(nextInheritedCheckboxValue(props.value));
                }}
                readOnly
            />
            <Label htmlFor={props.id}>{props.label}</Label>
        </div>
    );
}

function renderOrganizationOmnisearchCard(
    organization: OrganizationListItem | OrganizationOmnisearchListItem
): ReactElement {
    const businessDetailsSummary: string =
        organization.businessDetails === undefined
            ? 'Business details not set'
            : organization.businessDetails.legalEntityName;
    const merchantAccountTupleLines: ReadonlyArray<string> =
        organization.merchant_accounts === undefined || organization.merchant_accounts.length === 0
            ? ['No merchant accounts']
            : organization.merchant_accounts.map(
                  (merchantAccount) =>
                      `(${merchantAccount.processor_mid}, ${merchantAccount.surcharge_enabled}, ${merchantAccount.surcharge_pct})`
              );
    return (
        <div className="flex justify-between items-start gap-4">
            <div className="space-y-1">
                <div className="font-medium">{organization.name}</div>
                <div className="text-xs text-muted-foreground">{businessDetailsSummary}</div>
            </div>
            <div className="text-xs text-muted-foreground text-right space-y-0.5">
                {merchantAccountTupleLines.map((tupleLine: string) => (
                    <div key={tupleLine}>{tupleLine}</div>
                ))}
            </div>
        </div>
    );
}

function renderMerchantOrganizationOmnisearchCard(
    merchantOrganization: MerchantOrganizationListItem
): ReactElement {
    const linkedMerchantAccounts: ReadonlyArray<string> = merchantOrganization.merchant_accounts
        .slice(0, 2)
        .map((merchantAccount) => `${merchantAccount.processor_name} / ${merchantAccount.processor_mid}`);
    const hasMoreLinkedMerchantAccounts: boolean = merchantOrganization.merchant_accounts.length > 2;
    const linkedMerchantAccountsSummary: string =
        linkedMerchantAccounts.length === 0
            ? 'No merchant accounts'
            : hasMoreLinkedMerchantAccounts
              ? `${linkedMerchantAccounts.join(', ')} +${merchantOrganization.merchant_accounts.length - 2} more`
              : linkedMerchantAccounts.join(', ');
    return (
        <div className="space-y-1">
            <div className="font-medium">{merchantOrganization.name}</div>
            <div className="text-xs text-muted-foreground">
                Accounts: {linkedMerchantAccountsSummary}
            </div>
        </div>
    );
}

function renderMerchantAccountOmnisearchCard(
    merchantAccount: MerchantAccountSearchListItem
): ReactElement {
    const businessDetailsSummary: string =
        merchantAccount.businessDetails === undefined
            ? 'Business details not set'
            : merchantAccount.businessDetails.legalEntityName;
    return (
        <div className="space-y-1">
            <div className="font-medium">
                {merchantAccount.processor_name} / {merchantAccount.processor_mid}
            </div>
            <div className="text-xs text-muted-foreground">
                Merchant: {merchantAccount.organization_name}
            </div>
            <div className="text-xs text-muted-foreground">{businessDetailsSummary}</div>
        </div>
    );
}

function safeJsonStringifyForFilter(value: unknown): string {
    try {
        return JSON.stringify(value);
    } catch {
        return '';
    }
}

const allIpgSystemIds: ReadonlyArray<IpgSystemId> = SupportedIpgSystemIds;

function ConfigurationEditorShell(props: {
    title: string;
    canEdit: boolean;
    isEditing: boolean;
    onEdit: () => void;
    onCancel: () => void;
    onSave: () => void;
    isSaving: boolean;
    isSaveDisabled?: boolean;
    children: ReactElement;
}): ReactElement {
    useUnsavedChangesRegistration(props.isEditing);

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{props.title}</CardTitle>
                {props.canEdit && !props.isEditing && <Button onClick={props.onEdit}>Edit</Button>}
            </CardHeader>
            <CardContent>
                {props.children}
                {props.canEdit && props.isEditing && (
                    <div className="mt-4 flex gap-2">
                        <Button
                            onClick={props.onSave}
                            disabled={props.isSaving || (props.isSaveDisabled ?? false)}
                        >
                            {props.isSaving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button variant="outline" onClick={props.onCancel} disabled={props.isSaving}>
                            Cancel
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function SecretAssociationCard(props: {
    cardTitle: string;
    entityLabelForDialog: string;
    organizationUuid: string;
    organizationName: string;
    requiredCredentialType: CredentialType;
    attachedSecretIdentity: Pick<SecretListItem, 'credential_uuid'> | null;
    canEdit: boolean;
    credentialTargetFamily?: CredentialTargetFamily;
    credentialSystemSelectLabel?: string;
    availableCredentialSystemIds?: readonly SupportedCredentialSystemId[];
    onAttachSecret: (secret: Pick<SecretListItem, 'credential_uuid'>) => void;
    onDetachSecret: () => void;
    onAssociationChanged?: () => Promise<void> | void;
    isMutating: boolean;
}): ReactElement {
    const queryClient = useQueryClient();
    const [isSelectingSecret, setIsSelectingSecret] = useState<boolean>(false);
    const [isSecretModalOpen, setIsSecretModalOpen] = useState<boolean>(false);
    const [secretModalMode, setSecretModalMode] = useState<'view' | 'edit' | 'create'>('create');
    const [selectedSecret, setSelectedSecret] = useState<SecretListItem | undefined>(undefined);
    const [isResetConfirmationOpen, setIsResetConfirmationOpen] = useState<boolean>(false);
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);
    const [attachErrorMessage, setAttachErrorMessage] = useState<string | null>(null);
    const [secretToDelete, setSecretToDelete] = useState<SecretListItem | null>(null);
    const [selectedDeleteUsageKeys, setSelectedDeleteUsageKeys] = useState<string[]>([]);
    const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState<boolean>(false);
    const [secretToDisconnect, setSecretToDisconnect] = useState<SecretListItem | null>(null);
    const [selectedUsageKeys, setSelectedUsageKeys] = useState<string[]>([]);
    const [deleteConflictUsages, setDeleteConflictUsages] = useState<CredentialUsageListItem[] | null>(
        null
    );
    const hasUnsavedChanges: boolean = isSecretModalOpen && secretModalMode !== 'view';

    useUnsavedChangesRegistration(hasUnsavedChanges);

    const availableSecretsQueryKey: ReadonlyArray<string | number> = [
        'paymentsInfrastructure',
        'secretAssociation',
        props.organizationUuid,
        props.requiredCredentialType,
    ];
    const availableSecretsQuery = useQuery<ReadonlyArray<SecretListItem>>({
        queryKey: availableSecretsQueryKey,
        queryFn: async () => {
            const response = await secretsApi.listSecrets(
                { organization_uuid: props.organizationUuid },
                {
                    actions: undefined,
                    credential_type: props.requiredCredentialType,
                }
            );
            return filterAvailableSecretsForAssociation(response.secrets);
        },
    });

    const attachedSecretIdentity: Pick<SecretListItem, 'credential_uuid'> | null =
        props.attachedSecretIdentity;
    const attachedSecret: SecretListItem | null = findAttachedSecretByIdentity(
        availableSecretsQuery.data,
        attachedSecretIdentity
    );
    const deleteMutation = useDeleteSecret();
    const disableMutation = useDisableSecret();
    const enableMutation = useEnableSecret();
    const disconnectMutation = useDisconnectCredentialUsages();
    const credentialForDisconnectDialog: Pick<SecretIdentityReference, 'credential_uuid'> | undefined =
        isDisconnectDialogOpen && secretToDisconnect !== null
            ? {
                  credential_uuid: secretToDisconnect.credential_uuid,
              }
            : undefined;
    const credentialForDeleteDialog: Pick<SecretIdentityReference, 'credential_uuid'> | undefined =
        isDeleteConfirmationOpen && secretToDelete !== null
            ? {
                  credential_uuid: secretToDelete.credential_uuid,
              }
            : undefined;
    const {
        data: credentialUsagesResponse,
        isLoading: isCredentialUsagesLoading,
        error: credentialUsagesError,
    } = useCredentialUsages(credentialForDisconnectDialog);
    const {
        data: deleteCredentialUsagesResponse,
        isLoading: isDeleteCredentialUsagesLoading,
        error: deleteCredentialUsagesError,
    } = useCredentialUsages(credentialForDeleteDialog);

    const invalidateAvailableSecretsQuery = async (): Promise<void> => {
        await queryClient.invalidateQueries({
            queryKey: availableSecretsQueryKey,
        });
    };

    const handleViewSecret = (secret: SecretListItem): void => {
        setSelectedSecret(secret);
        setSecretModalMode('view');
        setIsSecretModalOpen(true);
    };

    const handleEditSecret = (secret: SecretListItem): void => {
        setSelectedSecret(secret);
        setSecretModalMode('edit');
        setIsSecretModalOpen(true);
    };

    const handleCreateSecret = (): void => {
        setSelectedSecret(undefined);
        setSecretModalMode('create');
        setIsSecretModalOpen(true);
    };

    const handleDisableSecret = (secret: SecretListItem): void => {
        disableMutation.mutate(
            {
                secret: {
                    credential_uuid: secret.credential_uuid,
                },
                organization: {
                    organization_uuid: props.organizationUuid,
                },
            },
            {
                onSuccess: () => {
                    runInBackground(invalidateAvailableSecretsQuery(), () => undefined);
                },
            }
        );
    };

    const handleEnableSecret = (secret: SecretListItem): void => {
        enableMutation.mutate(
            {
                secret: {
                    credential_uuid: secret.credential_uuid,
                },
                organization: {
                    organization_uuid: props.organizationUuid,
                },
            },
            {
                onSuccess: () => {
                    runInBackground(invalidateAvailableSecretsQuery(), () => undefined);
                },
            }
        );
    };

    const handleDeleteClick = (secret: SecretListItem): void => {
        setSecretToDelete(secret);
        setDeleteConflictUsages(null);
        setSelectedDeleteUsageKeys([]);
        setIsDeleteConfirmationOpen(true);
    };

    const handleConfirmDelete = (): void => {
        if (secretToDelete === null) {
            return;
        }
        deleteMutation.mutate(
            {
                secret: {
                    credential_uuid: secretToDelete.credential_uuid,
                },
                organization: {
                    organization_uuid: props.organizationUuid,
                },
            },
            {
                onSuccess: () => {
                    setIsDeleteConfirmationOpen(false);
                    setSecretToDelete(null);
                    setDeleteConflictUsages(null);
                    setSelectedDeleteUsageKeys([]);
                    runInBackground(invalidateAvailableSecretsQuery(), () => undefined);
                },
                onError: (deleteError: unknown) => {
                    const conflictUsages: CredentialUsageListItem[] | null =
                        parseCredentialDeleteConflictUsages(deleteError);
                    if (conflictUsages !== null) {
                        setDeleteConflictUsages(conflictUsages);
                    }
                },
            }
        );
    };

    const handleDisconnectClick = (secret: SecretListItem): void => {
        setSecretToDisconnect(secret);
        setSelectedUsageKeys([]);
        setIsDisconnectDialogOpen(true);
    };

    const handleToggleUsageSelection = (
        usage: CredentialUsageReference | CredentialUsageListItem,
        checked: boolean
    ): void => {
        const usageSelectionKey: string = buildCredentialUsageSelectionKey(usage);
        if (checked) {
            setSelectedUsageKeys((currentSelection) =>
                currentSelection.includes(usageSelectionKey)
                    ? currentSelection
                    : [...currentSelection, usageSelectionKey]
            );
            return;
        }
        setSelectedUsageKeys((currentSelection) =>
            currentSelection.filter((currentKey) => currentKey !== usageSelectionKey)
        );
    };

    const handleConfirmDisconnect = (): void => {
        if (secretToDisconnect === null) {
            return;
        }
        const availableUsages: CredentialUsageListItem[] = credentialUsagesResponse?.usages ?? [];
        const selectedUsages: CredentialUsageReference[] = availableUsages
            .filter((usage) => selectedUsageKeys.includes(buildCredentialUsageSelectionKey(usage)))
            .map(toCredentialUsageReference);
        if (selectedUsages.length === 0) {
            return;
        }
        disconnectMutation.mutate(
            {
                secret: {
                    credential_uuid: secretToDisconnect.credential_uuid,
                },
                organization: {
                    organization_uuid: props.organizationUuid,
                },
                usages: selectedUsages,
            },
            {
                onSuccess: () => {
                    setIsDisconnectDialogOpen(false);
                    setSecretToDisconnect(null);
                    setSelectedUsageKeys([]);
                    runInBackground(invalidateAvailableSecretsQuery(), () => undefined);
                    if (props.onAssociationChanged !== undefined) {
                        runInBackground(Promise.resolve(props.onAssociationChanged()), () => undefined);
                    }
                },
            }
        );
    };

    const deleteCredentialUsages: CredentialUsageListItem[] =
        deleteConflictUsages ?? deleteCredentialUsagesResponse?.usages ?? [];
    const getSystemName = (systemId: SecretListItem['system_id']): string => {
        const systemName = ThirdPartySystemIdToName[systemId];
        if (systemName !== undefined) {
            return systemName
                .toLowerCase()
                .split('_')
                .map((segment) => {
                    if (segment.length <= 2) {
                        return segment.toUpperCase();
                    }
                    return segment.charAt(0).toUpperCase() + segment.slice(1);
                })
                .join(' ');
        }
        return `System ${systemId}`;
    };

    const handleToggleDeleteUsageSelection = (
        usage: CredentialUsageReference | CredentialUsageListItem,
        checked: boolean
    ): void => {
        const usageSelectionKey: string = buildCredentialUsageSelectionKey(usage);
        if (checked) {
            setSelectedDeleteUsageKeys((currentSelection) =>
                currentSelection.includes(usageSelectionKey)
                    ? currentSelection
                    : [...currentSelection, usageSelectionKey]
            );
            return;
        }
        setSelectedDeleteUsageKeys((currentSelection) =>
            currentSelection.filter((currentKey) => currentKey !== usageSelectionKey)
        );
    };

    const handleToggleDeleteSelectAll = (checked: boolean): void => {
        if (!checked) {
            setSelectedDeleteUsageKeys([]);
            return;
        }
        setSelectedDeleteUsageKeys(
            deleteCredentialUsages.map((usage) => buildCredentialUsageSelectionKey(usage))
        );
    };

    const handleRemoveDeleteUsages = (): void => {
        if (secretToDelete === null) {
            return;
        }
        const selectedUsages: CredentialUsageReference[] = deleteCredentialUsages
            .filter((usage) =>
                selectedDeleteUsageKeys.includes(buildCredentialUsageSelectionKey(usage))
            )
            .map(toCredentialUsageReference);
        if (selectedUsages.length === 0) {
            return;
        }
        disconnectMutation.mutate(
            {
                secret: {
                    credential_uuid: secretToDelete.credential_uuid,
                },
                organization: {
                    organization_uuid: props.organizationUuid,
                },
                usages: selectedUsages,
            },
            {
                onSuccess: () => {
                    setSelectedDeleteUsageKeys([]);
                    setDeleteConflictUsages(null);
                    runInBackground(invalidateAvailableSecretsQuery(), () => undefined);
                    if (props.onAssociationChanged !== undefined) {
                        runInBackground(
                            Promise.resolve(props.onAssociationChanged()),
                            () => undefined
                        );
                    }
                },
            }
        );
    };

    return (
        <>
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>{props.cardTitle}</CardTitle>
                    {props.canEdit && attachedSecretIdentity === null && (
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setIsSelectingSecret((currentValue: boolean) => !currentValue)}
                            >
                                Select Credentials
                            </Button>
                            <Button size="sm" onClick={handleCreateSecret}>
                                Create Credentials
                            </Button>
                        </div>
                    )}
                    {props.canEdit && attachedSecretIdentity !== null && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setIsResetConfirmationOpen(true)}
                            disabled={props.isMutating}
                        >
                            Reset Credentials
                        </Button>
                    )}
                </CardHeader>
                <CardContent className="space-y-4">
                    {deleteMutation.error !== null && (
                        <Alert variant="destructive">
                            <AlertDescription>
                                {deleteMutation.error instanceof Error
                                    ? deleteMutation.error.message
                                    : 'Failed to delete credential'}
                            </AlertDescription>
                        </Alert>
                    )}
                    {disableMutation.error !== null && (
                        <Alert variant="destructive">
                            <AlertDescription>
                                {disableMutation.error instanceof Error
                                    ? disableMutation.error.message
                                    : 'Failed to disable credential'}
                            </AlertDescription>
                        </Alert>
                    )}
                    {enableMutation.error !== null && (
                        <Alert variant="destructive">
                            <AlertDescription>
                                {enableMutation.error instanceof Error
                                    ? enableMutation.error.message
                                    : 'Failed to enable credential'}
                            </AlertDescription>
                        </Alert>
                    )}
                    {disconnectMutation.error !== null && (
                        <Alert variant="destructive">
                            <AlertDescription>
                                {disconnectMutation.error instanceof Error
                                    ? disconnectMutation.error.message
                                    : 'Failed to disconnect credential usages'}
                            </AlertDescription>
                        </Alert>
                    )}
                    {attachedSecretIdentity === null ? (
                        <p className="text-muted-foreground">
                            No secret is currently attached to this {props.entityLabelForDialog}.
                        </p>
                    ) : (
                        <>
                            {availableSecretsQuery.isLoading && (
                                <p className="text-muted-foreground">Loading secret details...</p>
                            )}
                            {availableSecretsQuery.error !== null && (
                                <p className="text-destructive">{String(availableSecretsQuery.error)}</p>
                            )}
                            {attachedSecret !== null && (
                                <CredentialCardView
                                    credential={attachedSecret}
                                    showPublicFields={true}
                                    actions={
                                        <SecretActionButtons
                                            secret={attachedSecret}
                                            onView={() => handleViewSecret(attachedSecret)}
                                            onEdit={() => handleEditSecret(attachedSecret)}
                                            onDisable={() => handleDisableSecret(attachedSecret)}
                                            onEnable={() => handleEnableSecret(attachedSecret)}
                                            onDelete={() => handleDeleteClick(attachedSecret)}
                                            onDisconnect={() => handleDisconnectClick(attachedSecret)}
                                        />
                                    }
                                />
                            )}
                            {attachedSecret === null && availableSecretsQuery.isLoading === false && (
                                <p className="text-destructive">
                                    Attached secret is not available in this organization and
                                    credential type scope.
                                </p>
                            )}
                        </>
                    )}

                    {isSelectingSecret && props.canEdit && attachedSecretIdentity === null && (
                        <OmnisearchSelect<SecretListItem, string>
                            label="Select Credentials"
                            placeholder="Search secrets"
                            pageSize={1000}
                            fetchOnEmptyQuery={true}
                            selectedItemId={null}
                            fetchPage={async () => {
                                const secrets: ReadonlyArray<SecretListItem> =
                                    availableSecretsQuery.data ?? [];
                                return {
                                    items: secrets,
                                    pagination: {
                                        has_more: false,
                                    },
                                };
                            }}
                            getItemId={(secret) => secret.credential_uuid}
                            getItemLabel={(secret) =>
                                `${secret.account_id} (${CredentialTypeDisplayName[secret.credential_type]})`
                            }
                            getItemFilterText={(secret) =>
                                `${secret.account_id} ${secret.credential_uuid} ${safeJsonStringifyForFilter(secret.public_fields)}`.toLowerCase()
                            }
                            onSelect={(secret) => {
                                setAttachErrorMessage(null);
                                props.onAttachSecret(toSecretAssociationIdentity(secret));
                                setIsSelectingSecret(false);
                            }}
                            disabled={availableSecretsQuery.isLoading || props.isMutating}
                        />
                    )}

                    {attachErrorMessage !== null && (
                        <p className="text-destructive">{attachErrorMessage}</p>
                    )}

                    {props.isMutating && (
                        <p className="text-muted-foreground">
                            Updating secret association for this {props.entityLabelForDialog}...
                        </p>
                    )}
                </CardContent>
            </Card>

            <SecretModal
                open={isSecretModalOpen}
                onOpenChange={(open) => {
                    setIsSecretModalOpen(open);
                    if (!open) {
                        setSelectedSecret(undefined);
                        runInBackground(invalidateAvailableSecretsQuery(), () => undefined);
                    }
                }}
                organizationName={props.organizationName}
                organizationUuid={props.organizationUuid}
                secret={selectedSecret}
                mode={secretModalMode}
                lockedCredentialType={
                    secretModalMode === 'create' ? props.requiredCredentialType : undefined
                }
                credentialTargetFamily={props.credentialTargetFamily}
                credentialSystemSelectLabel={props.credentialSystemSelectLabel}
                availableCredentialSystemIds={props.availableCredentialSystemIds}
                onSecretSaved={(savedSecret: CreateSecretResponse) => {
                    runInBackground(
                        (async () => {
                            const refreshedSecrets = await queryClient.fetchQuery<
                                ReadonlyArray<SecretListItem>
                            >({
                                queryKey: availableSecretsQueryKey,
                                queryFn: async () => {
                                    const listResponse = await secretsApi.listSecrets(
                                        { organization_uuid: props.organizationUuid },
                                        {
                                            actions: undefined,
                                            credential_type: props.requiredCredentialType,
                                        }
                                    );
                                    return filterAvailableSecretsForAssociation(
                                        listResponse.secrets
                                    );
                                },
                            });
                            const createdSecret = refreshedSecrets.find(
                                (secret: SecretListItem) =>
                                    secret.system_id === savedSecret.system_id &&
                                    secret.account_id === savedSecret.account_id &&
                                    secret.credential_type === savedSecret.credential_type
                            );
                            if (createdSecret === undefined) {
                                setAttachErrorMessage(
                                    'Created secret was not found after refresh.'
                                );
                                return;
                            }
                            setAttachErrorMessage(null);
                            props.onAttachSecret(toSecretAssociationIdentity(createdSecret));
                        })(),
                        (error: unknown) => {
                            setAttachErrorMessage(
                                error instanceof Error
                                    ? error.message
                                    : 'Unable to attach the newly created secret.'
                            );
                        }
                    );
                }}
            />

            <ConfirmationModal
                open={isResetConfirmationOpen}
                onOpenChange={setIsResetConfirmationOpen}
                message={`Do you want to detach this ${props.entityLabelForDialog} for this set of credentials? The credentials will not be deleted.`}
                onConfirm={() => {
                    props.onDetachSecret();
                    setIsResetConfirmationOpen(false);
                }}
                confirmLabel="Confirm"
                cancelLabel="Cancel"
                isLoading={props.isMutating}
            />

            <CredentialDeleteDialog
                open={isDeleteConfirmationOpen}
                onOpenChange={(open) => {
                    setIsDeleteConfirmationOpen(open);
                    if (!open) {
                        setSecretToDelete(null);
                        setDeleteConflictUsages(null);
                        setSelectedDeleteUsageKeys([]);
                    }
                }}
                credentialLabel={
                    secretToDelete === null
                        ? ''
                        : `${getSystemName(secretToDelete.system_id)} (${secretToDelete.account_id})`
                }
                usages={deleteCredentialUsages}
                isLoadingUsages={isDeleteCredentialUsagesLoading}
                usageLoadError={deleteCredentialUsagesError}
                selectedUsageKeys={selectedDeleteUsageKeys}
                onToggleUsageSelection={handleToggleDeleteUsageSelection}
                onToggleSelectAll={handleToggleDeleteSelectAll}
                onConfirm={handleConfirmDelete}
                onRemove={handleRemoveDeleteUsages}
                isDeleting={deleteMutation.isPending}
                isRemoving={disconnectMutation.isPending}
            />

            <CredentialDisconnectDialog
                open={isDisconnectDialogOpen}
                onOpenChange={(open) => {
                    setIsDisconnectDialogOpen(open);
                    if (!open) {
                        setSecretToDisconnect(null);
                        setSelectedUsageKeys([]);
                    }
                }}
                usages={credentialUsagesResponse?.usages ?? []}
                isLoadingUsages={isCredentialUsagesLoading}
                usageLoadError={credentialUsagesError}
                selectedUsageKeys={selectedUsageKeys}
                onToggleUsageSelection={handleToggleUsageSelection}
                onConfirm={handleConfirmDisconnect}
                isDisconnecting={disconnectMutation.isPending}
            />
        </>
    );
}

export function CreateProcessorPage(): ReactElement {
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const activeRoleAssignmentState = useActiveRoleAssignmentState();
    const canView: boolean = hasPaymentsInfrastructurePage(activeRoleAssignmentState.activeRoleAssignment ?? null);
    const canCreate: boolean = hasInternalAdminPrivileges(activeRoleAssignmentState.activeRoleAssignment ?? null);
    const [editorValue, setEditorValue] = useState<ProcessorEditorValue>(
        createEmptyProcessorEditorValue()
    );
    const createValidationMessage: string | null = validateProcessorEditorValue(editorValue);

    const createMutation = useMutation({
        mutationFn: async () => {
            const request: CreateProcessorRequest = buildProcessorCreateRequest(editorValue);
            return await paymentsInfrastructureApi.createProcessor(request);
        },
        onSuccess: (processor: GetProcessorResponse) => {
            breadcrumbNavigate({
                source: 'terminal',
                target: {
                    url: `/payments-infrastructure/processors/${encodeURIComponent(processor.processor_name)}`,
                    title: 'Processor Details',
                },
            });
        },
    });

    if (activeRoleAssignmentState.isLoading) {
        return <div>Loading...</div>;
    }
    if (!canView || !canCreate) {
        return <div className="text-red-600">Access denied. Insufficient permissions.</div>;
    }

    return (
        <div className="space-y-4">
            <BackButton href="/payments-infrastructure/processors" tooltip="Back to Processors" />
            <Card>
                <CardHeader>
                    <CardTitle>Create Processor</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <ProcessorEditor value={editorValue} onChange={setEditorValue} />
                    {createValidationMessage !== null && (
                        <p className="text-sm text-muted-foreground">{createValidationMessage}</p>
                    )}
                    {createMutation.error !== null && (
                        <p className="text-destructive">{String(createMutation.error)}</p>
                    )}
                    <Button
                        onClick={() => createMutation.mutate()}
                        disabled={createMutation.isPending || createValidationMessage !== null}
                    >
                        {createMutation.isPending ? 'Creating...' : 'Create Processor'}
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
}

export function ProcessorDetailPage(): ReactElement {
    const params = useParams();
    const processorNameParam: string | undefined = params.processorName;
    if (processorNameParam === undefined) {
        throw new Error('Missing processorName route parameter');
    }
    const processorName: string = decodeURIComponent(processorNameParam);
    const activeRoleAssignmentState = useActiveRoleAssignmentState();
    const canView: boolean = hasPaymentsInfrastructurePage(activeRoleAssignmentState.activeRoleAssignment ?? null);
    const canEdit: boolean = hasInternalAdminPrivileges(activeRoleAssignmentState.activeRoleAssignment ?? null);
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editorValue, setEditorValue] = useState<ProcessorEditorValue>(
        createEmptyProcessorEditorValue()
    );
    const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);

    const processorQuery = useQuery<GetProcessorResponse>({
        queryKey: ['paymentsInfrastructure', 'processor', processorName],
        queryFn: async () => {
            return await paymentsInfrastructureApi.getProcessor(processorName);
        },
        enabled: canView,
    });

    useEffect(() => {
        if (processorQuery.data === undefined || isEditing) {
            return;
        }
        setEditorValue(createProcessorEditorValueFromResponse(processorQuery.data));
    }, [processorQuery.data, isEditing]);

    const saveValidationMessage: string | null = validateProcessorEditorValue(editorValue);

    const updateMutation = useMutation({
        mutationFn: async () => {
            return await paymentsInfrastructureApi.updateProcessor(processorName, {
                processor_name: editorValue.processorName.trim(),
                processor_config: buildProcessorConfig(editorValue),
            });
        },
        onSuccess: async (updatedProcessor: GetProcessorResponse) => {
            setSaveErrorMessage(null);
            setIsEditing(false);
            await queryClient.invalidateQueries({
                queryKey: ['paymentsInfrastructure', 'processors'],
            });
            await queryClient.invalidateQueries({
                queryKey: ['paymentsInfrastructure', 'processor', processorName],
            });
            const nextProcessorName: string = updatedProcessor.processor_name;
            if (nextProcessorName !== processorName) {
                runInBackground(
                    navigate(
                        `/payments-infrastructure/processors/${encodeURIComponent(nextProcessorName)}`
                    ),
                    (error: unknown) => {
                        throw error;
                    }
                );
                return;
            }
            await queryClient.invalidateQueries({
                queryKey: ['paymentsInfrastructure', 'processor', nextProcessorName],
            });
        },
        onError: (error: unknown) => {
            setSaveErrorMessage(
                error instanceof Error ? error.message : 'Failed to update processor.'
            );
        },
    });
    const lifecycleMutation = useMutation({
        mutationFn: async (status: Status) => {
            return await paymentsInfrastructureApi.updateProcessor(processorName, {
                status,
            });
        },
        onSuccess: async (updatedProcessor: GetProcessorResponse) => {
            setSaveErrorMessage(null);
            setIsEditing(false);
            await queryClient.invalidateQueries({
                queryKey: ['paymentsInfrastructure', 'processors'],
            });
            queryClient.setQueryData(
                ['paymentsInfrastructure', 'processor', processorName],
                updatedProcessor
            );
        },
        onError: (error: unknown) => {
            setSaveErrorMessage(
                error instanceof Error ? error.message : 'Failed to update processor.'
            );
        },
    });

    if (activeRoleAssignmentState.isLoading || processorQuery.isLoading) {
        return <div>Loading...</div>;
    }
    if (!canView) {
        return <div className="text-red-600">Access denied. Insufficient permissions.</div>;
    }
    if (processorQuery.error !== null) {
        if (processorQuery.error instanceof ApiError && processorQuery.error.status === 404) {
            return <div>Processor not found.</div>;
        }
        return (
            <Alert variant="destructive">
                <AlertDescription>
                    {processorQuery.error instanceof Error
                        ? processorQuery.error.message
                        : 'Failed to load processor.'}
                </AlertDescription>
            </Alert>
        );
    }
    if (processorQuery.data === undefined) {
        return <div>Processor not found.</div>;
    }

    return (
        <div className="space-y-4">
            <BackButton href="/payments-infrastructure/processors" tooltip="Back to Processors" />
            <div className="flex items-center justify-between gap-3">
                <h1 className="text-2xl font-semibold">{processorQuery.data.processor_name}</h1>
                <StatusBadge status={processorQuery.data.status} />
            </div>
            <ConfigurationEditorShell
                title="Processor Details"
                canEdit={canEdit && processorQuery.data.status !== Status.DELETED}
                isEditing={isEditing}
                onEdit={() => {
                    setSaveErrorMessage(null);
                    setIsEditing(true);
                }}
                onCancel={() => {
                    setSaveErrorMessage(null);
                    setIsEditing(false);
                    setEditorValue(createProcessorEditorValueFromResponse(processorQuery.data));
                }}
                onSave={() => updateMutation.mutate()}
                isSaving={updateMutation.isPending}
                isSaveDisabled={saveValidationMessage !== null}
            >
                <div className="space-y-4">
                    {canEdit && !isEditing ? (
                        <div className="flex justify-end gap-2">
                            {processorQuery.data.status === Status.DELETED ? (
                                <Button
                                    variant="outline"
                                    onClick={() => lifecycleMutation.mutate(Status.ACTIVE)}
                                    disabled={lifecycleMutation.isPending || updateMutation.isPending}
                                >
                                    Undelete
                                </Button>
                            ) : processorQuery.data.status === Status.ACTIVE ? (
                                <>
                                    <Button
                                        variant="outline"
                                        onClick={() => lifecycleMutation.mutate(Status.DISABLED)}
                                        disabled={
                                            lifecycleMutation.isPending ||
                                            updateMutation.isPending
                                        }
                                    >
                                        Disable
                                    </Button>
                                    <Button
                                        variant="destructive"
                                        onClick={() => lifecycleMutation.mutate(Status.DELETED)}
                                        disabled={
                                            lifecycleMutation.isPending ||
                                            updateMutation.isPending
                                        }
                                    >
                                        Delete
                                    </Button>
                                </>
                            ) : processorQuery.data.status === Status.DISABLED ? (
                                <Button
                                    variant="outline"
                                    onClick={() => lifecycleMutation.mutate(Status.ACTIVE)}
                                    disabled={lifecycleMutation.isPending || updateMutation.isPending}
                                >
                                    Re-enable
                                </Button>
                            ) : null}
                        </div>
                    ) : null}
                    <ProcessorEditor
                        value={editorValue}
                        onChange={(nextValue: ProcessorEditorValue) => {
                            setSaveErrorMessage(null);
                            setEditorValue(nextValue);
                        }}
                        disabled={
                            !isEditing || updateMutation.isPending || lifecycleMutation.isPending
                        }
                    />
                    {saveValidationMessage !== null && isEditing && (
                        <p className="text-sm text-muted-foreground">{saveValidationMessage}</p>
                    )}
                    {saveErrorMessage !== null && (
                        <p className="text-destructive">{saveErrorMessage}</p>
                    )}
                </div>
            </ConfigurationEditorShell>
        </div>
    );
}

export function CreateMerchantAccountPage(): ReactElement {
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const [searchParams] = useSearchParams();
    const lockedOrganizationUuid: string | null =
        parseLockedOrganizationUuidSearchParam(searchParams);
    const activeRoleAssignmentState = useActiveRoleAssignmentState();
    const canView: boolean = hasPaymentsInfrastructurePage(activeRoleAssignmentState.activeRoleAssignment ?? null);
    const canCreate: boolean = hasInternalAdminPrivileges(activeRoleAssignmentState.activeRoleAssignment ?? null);
    const [processorName, setProcessorName] = useState<string>('');
    const [processorMid, setProcessorMid] = useState<string>('');
    const [organizationUuid, setOrganizationUuid] = useState<string | null>(
        lockedOrganizationUuid
    );
    const [allowSurcharge, setAllowSurcharge] = useState<boolean>(false);
    const [allowConvenienceFee, setAllowConvenienceFee] = useState<boolean>(false);
    const [allowOverrideFee, setAllowOverrideFee] = useState<boolean>(false);
    const [addressRequiredForCardPayment, setAddressRequiredForCardPayment] =
        useState<boolean>(false);
    const [surchargePct, setSurchargePct] = useState<string>('0');
    const [convenienceFee, setConvenienceFee] = useState<string>('0');
    const isMerchantSelectionLocked: boolean = lockedOrganizationUuid !== null;
    const cancelTarget = isMerchantSelectionLocked
        ? {
              url: `/organizations/${lockedOrganizationUuid}`,
              title: 'Organization Details',
          }
        : {
              url: '/payments-infrastructure/merchant-accounts',
              title: 'Merchant Accounts',
          };

    useEffect(() => {
        if (lockedOrganizationUuid !== null) {
            setOrganizationUuid(lockedOrganizationUuid);
        }
    }, [lockedOrganizationUuid]);

    const lockedOrganizationQuery = useQuery<GetOrganizationResponse>({
        queryKey: ['organization', lockedOrganizationUuid ?? 'none'],
        queryFn: async () => {
            if (lockedOrganizationUuid === null) {
                throw new Error('Locked merchant UUID is required');
            }
            return await organizationApi.getOrganization(lockedOrganizationUuid);
        },
        enabled: canView && lockedOrganizationUuid !== null,
    });
    const lockedOrganizationSelectedLabel: string | undefined =
        lockedOrganizationQuery.data === undefined
            ? lockedOrganizationUuid ?? undefined
            : `${lockedOrganizationQuery.data.name} (${lockedOrganizationQuery.data.organization_uuid})`;
    const processorsQuery = useQuery<ProcessorListItem[]>({
        queryKey: ['paymentsInfrastructure', 'processors', 'all'],
        queryFn: async () => {
            const processorSearchResponse = await paymentsInfrastructureApi.searchProcessors({
                limit: 100,
            });
            const sortedProcessors: ProcessorListItem[] = [
                ...selectActiveProcessors(processorSearchResponse.processors),
            ].sort((leftProcessor: ProcessorListItem, rightProcessor: ProcessorListItem) =>
                leftProcessor.processor_name.localeCompare(rightProcessor.processor_name)
            );
            return sortedProcessors;
        },
        enabled: canView,
    });

    const createMutation = useMutation({
        mutationFn: async () => {
            if (organizationUuid === null) {
                throw new Error('Merchant selection is required');
            }
            const request: CreateMerchantAccountRequest = {
                processor_name: processorName.trim(),
                processor_mid: processorMid.trim(),
                organization_uuid: organizationUuid,
                merchant_account_configuration: {
                    allow_surcharge: allowSurcharge,
                    allow_convenience_fee: allowConvenienceFee,
                    allow_override_fee: allowOverrideFee,
                    addressRequiredForCardPayment,
                    surcharge_pct: Number(surchargePct),
                    convenience_fee: Number(convenienceFee),
                },
            };
            return await paymentsInfrastructureApi.createMerchantAccount(request);
        },
        onSuccess: (merchantAccount: GetMerchantAccountResponse) => {
            breadcrumbNavigate({
                source: 'terminal',
                target: {
                    url: `/payments-infrastructure/merchant-accounts/${merchantAccount.merchant_account_uuid}`,
                    title: 'Merchant Account Details',
                },
            });
        },
    });

    if (activeRoleAssignmentState.isLoading) {
        return <div>Loading...</div>;
    }
    if (!canView || !canCreate) {
        return <div className="text-red-600">Access denied. Insufficient permissions.</div>;
    }

    return (
        <div className="space-y-4">
            <BackButton
                href="/payments-infrastructure/merchant-accounts"
                tooltip="Back to Merchant Accounts"
            />
            <Card>
                <CardHeader>
                    <CardTitle>Create Merchant Account</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div>
                        <Label htmlFor="processor_name">Processor Name</Label>
                        <select
                            id="processor_name"
                            value={processorName}
                            onChange={(event) => setProcessorName(event.target.value)}
                            className="w-full border rounded px-3 py-2"
                            disabled={processorsQuery.isLoading || createMutation.isPending}
                        >
                            <option value="">Select processor...</option>
                            {(processorsQuery.data ?? []).map((processor: ProcessorListItem) => (
                                <option
                                    key={processor.processor_name}
                                    value={processor.processor_name}
                                >
                                    {processor.processor_name}
                                </option>
                            ))}
                        </select>
                        {processorsQuery.error !== null && (
                            <p className="text-destructive">{String(processorsQuery.error)}</p>
                        )}
                    </div>
                    <div>
                        <Label htmlFor="processor_mid">Processor MID</Label>
                        <Input
                            id="processor_mid"
                            value={processorMid}
                            onChange={(event) => setProcessorMid(event.target.value)}
                        />
                    </div>
                    <div>
                        <OmnisearchSelect<OrganizationOmnisearchListItem, string>
                            label="Merchant"
                            placeholder={
                                isMerchantSelectionLocked
                                    ? 'Merchant selected from referring page'
                                    : 'Search merchants...'
                            }
                            pageSize={50}
                            selectedItemId={organizationUuid}
                            selectedItemLabel={lockedOrganizationSelectedLabel}
                            disabled={isMerchantSelectionLocked}
                            onClearSelection={() => {
                                if (!isMerchantSelectionLocked) {
                                    setOrganizationUuid(null);
                                }
                            }}
                            fetchPage={async (params) => {
                                const response = await organizationApi.searchOrganizations({
                                    ...(params.terms !== undefined
                                        ? { terms: [...params.terms] }
                                        : {}),
                                    is_merchant: true,
                                    limit: params.limit,
                                    cursor: params.cursor,
                                });
                                return {
                                    items: response.organizations,
                                    pagination: response.pagination,
                                };
                            }}
                            getItemId={(item) => item.organization_uuid}
                            getItemLabel={(item) => `${item.name} (${item.organization_uuid})`}
                            getItemFilterText={(item) =>
                                `${item.name} ${item.organization_uuid}`.toLowerCase()
                            }
                            renderItemCard={renderOrganizationOmnisearchCard}
                            onSelect={(item) => {
                                if (!isMerchantSelectionLocked) {
                                    setOrganizationUuid(item.organization_uuid);
                                }
                            }}
                        />
                        {lockedOrganizationQuery.error !== null && (
                            <p className="text-destructive">
                                {lockedOrganizationQuery.error instanceof Error
                                    ? lockedOrganizationQuery.error.message
                                    : 'Failed to load locked merchant.'}
                            </p>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="allow_surcharge"
                                checked={allowSurcharge}
                                onCheckedChange={(nextValue) => setAllowSurcharge(nextValue === true)}
                            />
                            <Label htmlFor="allow_surcharge">Allow surcharge</Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="allow_convenience_fee"
                                checked={allowConvenienceFee}
                                onCheckedChange={(nextValue) =>
                                    setAllowConvenienceFee(nextValue === true)
                                }
                            />
                            <Label htmlFor="allow_convenience_fee">Allow convenience fee</Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="allow_override_fee"
                                checked={allowOverrideFee}
                                onCheckedChange={(nextValue) =>
                                    setAllowOverrideFee(nextValue === true)
                                }
                            />
                            <Label htmlFor="allow_override_fee">Allow fee override</Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="address_required_for_card_payment"
                                checked={addressRequiredForCardPayment}
                                onCheckedChange={(nextValue) =>
                                    setAddressRequiredForCardPayment(nextValue === true)
                                }
                            />
                            <Label htmlFor="address_required_for_card_payment">
                                Address required for card payment
                            </Label>
                        </div>
                        <div>
                            <Label htmlFor="surcharge_pct">Surcharge %</Label>
                            <Input
                                id="surcharge_pct"
                                value={surchargePct}
                                onChange={(event) => setSurchargePct(event.target.value)}
                            />
                        </div>
                        <div>
                            <Label htmlFor="convenience_fee">Convenience fee</Label>
                            <Input
                                id="convenience_fee"
                                value={convenienceFee}
                                onChange={(event) => setConvenienceFee(event.target.value)}
                            />
                        </div>
                    </div>
                    {createMutation.error !== null && (
                        <p className="text-destructive">{String(createMutation.error)}</p>
                    )}
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() =>
                                breadcrumbNavigate({
                                    source: 'terminal',
                                    target: cancelTarget,
                                })
                            }
                            disabled={createMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => createMutation.mutate()}
                            disabled={
                                createMutation.isPending ||
                                lockedOrganizationQuery.isLoading ||
                                lockedOrganizationQuery.error !== null ||
                                processorName.trim().length === 0 ||
                                processorMid.trim().length === 0 ||
                                organizationUuid === null
                            }
                        >
                            {createMutation.isPending ? 'Creating...' : 'Create Merchant Account'}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export function MerchantAccountDetailPage(): ReactElement {
    const navigate = useNavigate();
    const params = useParams();
    const merchantAccountIdParam: string | undefined = params.merchantAccountId;
    if (merchantAccountIdParam === undefined) {
        throw new Error('Missing merchantAccountId route parameter');
    }
    const merchantAccountReference: string = merchantAccountIdParam;
    const queryClient = useQueryClient();
    const activeRoleAssignmentState = useActiveRoleAssignmentState();
    const canView: boolean = hasPaymentsInfrastructurePage(activeRoleAssignmentState.activeRoleAssignment ?? null);
    const canEdit: boolean = hasInternalAdminPrivileges(activeRoleAssignmentState.activeRoleAssignment ?? null);
    const [isEditingBasicInformation, setIsEditingBasicInformation] = useState<boolean>(false);
    const [draftProcessorName, setDraftProcessorName] = useState<string>('');
    const [draftProcessorMid, setDraftProcessorMid] = useState<string>('');
    const [draftAppliesToChildren, setDraftAppliesToChildren] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [draftSurchargePctText, setDraftSurchargePctText] = useState<string>('0');
    const [draftConvenienceFeeText, setDraftConvenienceFeeText] = useState<string>('0');
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);

    const detailQuery = useQuery<GetMerchantAccountResponse>({
        queryKey: ['paymentsInfrastructure', 'merchantAccount', merchantAccountReference],
        queryFn: async () => {
            return await paymentsInfrastructureApi.getMerchantAccount(merchantAccountReference);
        },
        enabled: canView,
    });
    const merchantOrganizationQuery = useQuery<GetOrganizationResponse>({
        queryKey: [
            'organization',
            detailQuery.data?.organization_uuid ?? 'none',
        ],
        queryFn: async () => {
            if (detailQuery.data === undefined) {
                throw new Error('Merchant account details are required');
            }
            return await organizationApi.getOrganization(detailQuery.data.organization_uuid);
        },
        enabled: detailQuery.data !== undefined,
    });

    const [draftConfiguration, setDraftConfiguration] = useState<MerchantAccountConfiguration>({
        allow_surcharge: false,
        allow_convenience_fee: false,
        allow_override_fee: false,
        addressRequiredForCardPayment: true,
        surcharge_pct: 0,
        convenience_fee: 0,
    });

    useUnsavedChangesRegistration(isEditingBasicInformation);

    useEffect(() => {
        if (detailQuery.data === undefined || isEditing) {
            return;
        }
        setDraftConfiguration(detailQuery.data.merchant_account_configuration);
        setDraftSurchargePctText(
            String(detailQuery.data.merchant_account_configuration.surcharge_pct)
        );
        setDraftConvenienceFeeText(
            String(detailQuery.data.merchant_account_configuration.convenience_fee)
        );
    }, [detailQuery.data, isEditing]);
    useEffect(() => {
        if (detailQuery.data === undefined || isEditingBasicInformation) {
            return;
        }
        setDraftProcessorName(detailQuery.data.processor_name);
        setDraftProcessorMid(detailQuery.data.processor_mid);
        setDraftAppliesToChildren(detailQuery.data.applies_to_children);
    }, [detailQuery.data, isEditingBasicInformation]);

    const updateMutation = useMutation({
        mutationFn: async () => {
            const surchargePctNumber: number = Number(draftSurchargePctText.trim());
            const convenienceFeeNumber: number = Number(draftConvenienceFeeText.trim());
            if (!Number.isFinite(surchargePctNumber) || !Number.isFinite(convenienceFeeNumber)) {
                throw new Error('Surcharge % and convenience fee must be valid numbers');
            }
            if (detailQuery.data === undefined) {
                throw new Error('Merchant account details are required');
            }
            return await paymentsInfrastructureApi.updateMerchantAccount(
                merchantAccountReference,
                {
                merchant_account_configuration: {
                    ...draftConfiguration,
                    surcharge_pct: surchargePctNumber,
                    convenience_fee: convenienceFeeNumber,
                },
                }
            );
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['paymentsInfrastructure', 'merchantAccount', merchantAccountReference],
            });
            setIsEditing(false);
        },
    });
    const updateBasicInformationMutation = useMutation({
        mutationFn: async () => {
            if (detailQuery.data === undefined) {
                throw new Error('Merchant account details are required');
            }
            return await paymentsInfrastructureApi.updateMerchantAccount(
                merchantAccountReference,
                {
                processor_name: draftProcessorName.trim(),
                processor_mid: draftProcessorMid.trim(),
                applies_to_children: draftAppliesToChildren,
                }
            );
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['paymentsInfrastructure', 'merchantAccount', merchantAccountReference],
            });
            setIsEditingBasicInformation(false);
        },
    });
    const deleteMutation = useMutation({
        mutationFn: async () => {
            if (detailQuery.data === undefined) {
                throw new Error('Merchant account details are required');
            }
            return await paymentsInfrastructureApi.deleteMerchantAccount(merchantAccountReference);
        },
        onSuccess: () => {
            runInBackground(navigate('/payments-infrastructure/merchant-accounts'), (error: unknown) => {
                throw error;
            });
        },
    });
    const undeleteMutation = useMutation({
        mutationFn: async () => {
            if (detailQuery.data === undefined) {
                throw new Error('Merchant account details are required');
            }
            return await paymentsInfrastructureApi.undeleteMerchantAccount(
                merchantAccountReference
            );
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['paymentsInfrastructure', 'merchantAccount', merchantAccountReference],
            });
        },
    });
    const lifecycleMutation = useMutation({
        mutationFn: async (status: Status) => {
            if (detailQuery.data === undefined) {
                throw new Error('Merchant account details are required');
            }
            return await paymentsInfrastructureApi.updateMerchantAccount(
                merchantAccountReference,
                { status }
            );
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['paymentsInfrastructure', 'merchantAccount', merchantAccountReference],
            });
        },
    });

    if (activeRoleAssignmentState.isLoading || detailQuery.isLoading) {
        return <div>Loading...</div>;
    }
    if (!canView) {
        return <div className="text-red-600">Access denied. Insufficient permissions.</div>;
    }
    if (detailQuery.data === undefined) {
        return <div>Merchant account not found.</div>;
    }

    const merchantAccount: GetMerchantAccountResponse = detailQuery.data;
    return (
        <div className="space-y-4">
            <BackButton
                href="/payments-infrastructure/merchant-accounts"
                tooltip="Back to Merchant Accounts"
            />
            <div className="mb-8 flex items-center justify-between gap-3">
                <h1 className="text-3xl font-bold">Merchant Account Details</h1>
                <StatusBadge status={merchantAccount.status} />
            </div>
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Basic Information</CardTitle>
                    {canEdit && !isEditingBasicInformation && (
                        <div className="flex gap-2">
                            {merchantAccount.status !== Status.DELETED ? (
                                <Button
                                    size="sm"
                                    onClick={() => setIsEditingBasicInformation(true)}
                                    disabled={lifecycleMutation.isPending || undeleteMutation.isPending}
                                >
                                    Edit
                                </Button>
                            ) : null}
                            {merchantAccount.status === Status.ACTIVE ? (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => lifecycleMutation.mutate(Status.DISABLED)}
                                    disabled={
                                        lifecycleMutation.isPending ||
                                        deleteMutation.isPending ||
                                        undeleteMutation.isPending
                                    }
                                >
                                    Disable
                                </Button>
                            ) : merchantAccount.status === Status.DISABLED ? (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => lifecycleMutation.mutate(Status.ACTIVE)}
                                    disabled={
                                        lifecycleMutation.isPending ||
                                        deleteMutation.isPending ||
                                        undeleteMutation.isPending
                                    }
                                >
                                    Re-enable
                                </Button>
                            ) : merchantAccount.status === Status.DELETED ? (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => undeleteMutation.mutate()}
                                    disabled={
                                        lifecycleMutation.isPending ||
                                        deleteMutation.isPending ||
                                        undeleteMutation.isPending
                                    }
                                >
                                    Undelete
                                </Button>
                            ) : null}
                            {merchantAccount.status !== Status.DELETED ? (
                                <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => setIsDeleteConfirmationOpen(true)}
                                    disabled={
                                        deleteMutation.isPending ||
                                        lifecycleMutation.isPending ||
                                        undeleteMutation.isPending
                                    }
                                >
                                    Delete
                                </Button>
                            ) : null}
                        </div>
                    )}
                    {canEdit && isEditingBasicInformation && (
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                onClick={() => updateBasicInformationMutation.mutate()}
                                disabled={
                                    updateBasicInformationMutation.isPending ||
                                    draftProcessorName.trim().length === 0 ||
                                    draftProcessorMid.trim().length === 0
                                }
                            >
                                {updateBasicInformationMutation.isPending ? 'Saving...' : 'Save'}
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                    setDraftProcessorName(merchantAccount.processor_name);
                                    setDraftProcessorMid(merchantAccount.processor_mid);
                                    setDraftAppliesToChildren(merchantAccount.applies_to_children);
                                    setIsEditingBasicInformation(false);
                                }}
                                disabled={updateBasicInformationMutation.isPending}
                            >
                                Cancel
                            </Button>
                        </div>
                    )}
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                    <div>
                        <p className="text-sm text-muted-foreground">Merchant</p>
                        {merchantOrganizationQuery.data === undefined ? (
                            <p className="font-semibold text-muted-foreground">Loading...</p>
                        ) : (
                            <p
                                className="font-semibold text-primary underline cursor-pointer"
                                onClick={() => {
                                    runInBackground(
                                        navigate(`/organizations/${merchantAccount.organization_uuid}`),
                                        (error: unknown) => {
                                            throw error;
                                        }
                                    );
                                }}
                            >
                                {merchantOrganizationQuery.data.name}
                            </p>
                        )}
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Processor Name</p>
                        {isEditingBasicInformation ? (
                            <Input
                                value={draftProcessorName}
                                onChange={(event) => setDraftProcessorName(event.target.value)}
                            />
                        ) : (
                            <button
                                type="button"
                                className="font-semibold text-primary underline cursor-pointer text-left"
                                onClick={() => {
                                    const processorDetailsUrl: string =
                                        `/payments-infrastructure/processors/${encodeURIComponent(
                                            merchantAccount.processor_name
                                        )}`;
                                    runInBackground(
                                        navigate(processorDetailsUrl),
                                        (error: unknown) => {
                                            throw error;
                                        }
                                    );
                                }}
                            >
                                {merchantAccount.processor_name}
                            </button>
                        )}
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Processor MID</p>
                        {isEditingBasicInformation ? (
                            <Input
                                value={draftProcessorMid}
                                onChange={(event) => setDraftProcessorMid(event.target.value)}
                            />
                        ) : (
                            <p className="font-semibold">{merchantAccount.processor_mid}</p>
                        )}
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Applies to children</p>
                        {isEditingBasicInformation ? (
                            <div className="flex items-center gap-2 pt-2">
                                <Checkbox
                                    id="merchant_account_applies_to_children"
                                    checked={draftAppliesToChildren}
                                    onCheckedChange={(nextValue) =>
                                        setDraftAppliesToChildren(nextValue === true)
                                    }
                                />
                                <Label htmlFor="merchant_account_applies_to_children">Enabled</Label>
                            </div>
                        ) : (
                            <p className="font-semibold">
                                {merchantAccount.applies_to_children ? 'Yes' : 'No'}
                            </p>
                        )}
                    </div>
                    {updateBasicInformationMutation.error !== null && (
                        <p className="text-destructive col-span-2">
                            {String(updateBasicInformationMutation.error)}
                        </p>
                    )}
                    {lifecycleMutation.error !== null && (
                        <p className="text-destructive col-span-2">
                            {lifecycleMutation.error instanceof Error
                                ? lifecycleMutation.error.message
                                : 'Failed to update merchant account status.'}
                        </p>
                    )}
                    {undeleteMutation.error !== null && (
                        <p className="text-destructive col-span-2">
                            {undeleteMutation.error instanceof Error
                                ? undeleteMutation.error.message
                                : 'Failed to undelete merchant account.'}
                        </p>
                    )}
                </CardContent>
            </Card>
            <ConfirmationModal
                open={isDeleteConfirmationOpen}
                onOpenChange={setIsDeleteConfirmationOpen}
                message="Delete this merchant account? This operation marks the status as DELETED."
                onConfirm={() => {
                    deleteMutation.mutate();
                }}
                onCancel={() => {
                    setIsDeleteConfirmationOpen(false);
                }}
                confirmLabel="Delete"
                isLoading={deleteMutation.isPending}
            />

            <ConfigurationEditorShell
                title="Configuration"
                canEdit={canEdit && merchantAccount.status !== Status.DELETED}
                isEditing={isEditing}
                onEdit={() => setIsEditing(true)}
                onCancel={() => {
                    setDraftConfiguration(merchantAccount.merchant_account_configuration);
                    setDraftSurchargePctText(
                        String(merchantAccount.merchant_account_configuration.surcharge_pct)
                    );
                    setDraftConvenienceFeeText(
                        String(merchantAccount.merchant_account_configuration.convenience_fee)
                    );
                    setIsEditing(false);
                }}
                onSave={() => updateMutation.mutate()}
                isSaving={updateMutation.isPending}
            >
                <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="detail_allow_surcharge"
                            checked={draftConfiguration.allow_surcharge}
                            disabled={!isEditing}
                            onCheckedChange={(nextValue) =>
                                setDraftConfiguration((currentValue: MerchantAccountConfiguration) => ({
                                    ...currentValue,
                                    allow_surcharge: nextValue === true,
                                }))
                            }
                        />
                        <Label htmlFor="detail_allow_surcharge">Allow surcharge</Label>
                    </div>
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="detail_allow_convenience_fee"
                            checked={draftConfiguration.allow_convenience_fee}
                            disabled={!isEditing}
                            onCheckedChange={(nextValue) =>
                                setDraftConfiguration((currentValue: MerchantAccountConfiguration) => ({
                                    ...currentValue,
                                    allow_convenience_fee: nextValue === true,
                                }))
                            }
                        />
                        <Label htmlFor="detail_allow_convenience_fee">Allow convenience fee</Label>
                    </div>
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="detail_allow_override_fee"
                            checked={draftConfiguration.allow_override_fee}
                            disabled={!isEditing}
                            onCheckedChange={(nextValue) =>
                                setDraftConfiguration((currentValue: MerchantAccountConfiguration) => ({
                                    ...currentValue,
                                    allow_override_fee: nextValue === true,
                                }))
                            }
                        />
                        <Label htmlFor="detail_allow_override_fee">Allow fee override</Label>
                    </div>
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="detail_address_required_for_card_payment"
                            checked={draftConfiguration.addressRequiredForCardPayment}
                            disabled={!isEditing}
                            onCheckedChange={(nextValue) =>
                                setDraftConfiguration((currentValue: MerchantAccountConfiguration) => ({
                                    ...currentValue,
                                    addressRequiredForCardPayment: nextValue === true,
                                }))
                            }
                        />
                        <Label htmlFor="detail_address_required_for_card_payment">
                            Address required for card payment
                        </Label>
                    </div>
                    <div>
                        <Label htmlFor="detail_surcharge_pct">Surcharge %</Label>
                        <Input
                            id="detail_surcharge_pct"
                            disabled={!isEditing}
                            value={draftSurchargePctText}
                            onChange={(event) => setDraftSurchargePctText(event.target.value)}
                        />
                    </div>
                    <div>
                        <Label htmlFor="detail_convenience_fee">Convenience fee</Label>
                        <Input
                            id="detail_convenience_fee"
                            disabled={!isEditing}
                            value={draftConvenienceFeeText}
                            onChange={(event) => setDraftConvenienceFeeText(event.target.value)}
                        />
                    </div>
                </div>
            </ConfigurationEditorShell>

            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Logical Terminals</CardTitle>
                    {canEdit && merchantAccount.status === Status.ACTIVE && (
                        <Button
                            size="sm"
                            onClick={() => {
                                runInBackground(
                                    navigate(
                                        `/payments-infrastructure/logical-terminals/create?merchant_account_uuid=${merchantAccount.merchant_account_uuid}`
                                    ),
                                    (error: unknown) => {
                                        throw error;
                                    }
                                );
                            }}
                        >
                            Create Logical Terminal
                        </Button>
                    )}
                </CardHeader>
                <CardContent>
                    <EmbeddedInfiniteScrollList<LogicalTerminalListItem, string, readonly unknown[]>
                        queryKeyPrefix={[
                            'paymentsInfrastructure',
                            'merchantAccount',
                            merchantAccount.merchant_account_uuid,
                            'logicalTerminals',
                        ]}
                        pageSize={50}
                        maxHeight="50rem"
                        enabled={canView}
                        parseCursorParam={parseOpaqueEmbeddedListCursorParam}
                        fetchPage={async ({ limit, cursor }) => {
                            const response = await paymentsInfrastructureApi.searchLogicalTerminals({
                                merchant_account_uuid: merchantAccount.merchant_account_uuid,
                                limit,
                                cursor,
                            });
                            return {
                                items: response.logical_terminals,
                                pagination: response.pagination,
                            };
                        }}
                        stateCopy={{
                            emptyLabel: 'No logical terminals configured for this merchant account.',
                        }}
                        renderItems={({ items }) => (
                            <div className="space-y-2">
                                {items.map((logicalTerminal: LogicalTerminalListItem) => (
                                    <div
                                        key={logicalTerminal.logical_terminal_uuid}
                                        className="border rounded p-3 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                                        onClick={() => {
                                            runInBackground(
                                                navigate(
                                                    `/payments-infrastructure/logical-terminals/${logicalTerminal.logical_terminal_uuid}`
                                                ),
                                                (error: unknown) => {
                                                    throw error;
                                                }
                                            );
                                        }}
                                    >
                                        <div>
                                            <div className="font-medium">
                                                {getLogicalTerminalDisplayLabel(logicalTerminal)}
                                            </div>
                                        </div>
                                        <StatusBadge status={logicalTerminal.status} />
                                    </div>
                                ))}
                            </div>
                        )}
                    />
                </CardContent>
            </Card>
        </div>
    );
}

export function CreateLogicalTerminalPage(): ReactElement {
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const [searchParams] = useSearchParams();
    const lockedMerchantAccountUuidRaw: string | null = searchParams.get('merchant_account_uuid');
    const lockedMerchantAccountUuid: string | null =
        lockedMerchantAccountUuidRaw === null
            ? null
            : MerchantAccountUuidSearchParamSchema.parse(lockedMerchantAccountUuidRaw);
    const activeRoleAssignmentState = useActiveRoleAssignmentState();
    const canView: boolean = hasPaymentsInfrastructurePage(activeRoleAssignmentState.activeRoleAssignment ?? null);
    const canCreate: boolean = hasInternalAdminPrivileges(activeRoleAssignmentState.activeRoleAssignment ?? null);
    const [merchantAccountReference, setMerchantAccountReference] = useState<string | null>(
        lockedMerchantAccountUuid
    );
    const [selectedMerchantOrganizationUuid, setSelectedMerchantOrganizationUuid] = useState<string | null>(
        null
    );
    const [name, setName] = useState<string>('');
    const [ipgSystemId, setIpgSystemId] = useState<IpgSystemId | null>(null);
    const [processorTid, setProcessorTid] = useState<string>('');
    const [defaultTerminalDeviceKind, setDefaultTerminalDeviceKind] = useState<
        'physical' | 'virtual' | null
    >('physical');
    const [surchargePct, setSurchargePct] = useState<string>('');
    const [convenienceFee, setConvenienceFee] = useState<string>('');
    const [allowOverrideFee, setAllowOverrideFee] = useState<boolean | undefined>(undefined);

    const selectedMerchantAccountQuery = useQuery<GetMerchantAccountResponse>({
        queryKey: ['paymentsInfrastructure', 'merchantAccount', merchantAccountReference],
        queryFn: async () => {
            if (merchantAccountReference === null) {
                throw new Error('Merchant account must be selected');
            }
            return await paymentsInfrastructureApi.getMerchantAccount(merchantAccountReference);
        },
        enabled: merchantAccountReference !== null,
    });
    const selectedMerchantAccountOrganizationQuery = useQuery<GetOrganizationResponse>({
        queryKey: [
            'organization',
            selectedMerchantAccountQuery.data?.organization_uuid ?? 'none',
            'forCreateLogicalTerminalMerchantSelection',
        ],
        queryFn: async () => {
            if (selectedMerchantAccountQuery.data === undefined) {
                throw new Error('Merchant account details are required');
            }
            return await organizationApi.getOrganization(
                selectedMerchantAccountQuery.data.organization_uuid
            );
        },
        enabled: selectedMerchantAccountQuery.data !== undefined,
    });
    const merchantSelectionLockedBySelectedMerchantAccount: boolean =
        selectedMerchantAccountQuery.data?.applies_to_children === false;
    const shouldRestrictMerchantAccountSearchByMerchantSelection: boolean =
        selectedMerchantOrganizationUuid !== null && merchantAccountReference === null;
    const effectiveMerchantOrganizationUuid: string | undefined =
        selectedMerchantOrganizationUuid ??
        selectedMerchantAccountQuery.data?.organization_uuid;
    const effectiveMerchantOrganizationQuery = useQuery<GetOrganizationResponse>({
        queryKey: ['organization', effectiveMerchantOrganizationUuid ?? 'none'],
        queryFn: async () => {
            if (effectiveMerchantOrganizationUuid === undefined) {
                throw new Error('Merchant organization must be selected');
            }
            return await organizationApi.getOrganization(effectiveMerchantOrganizationUuid);
        },
        enabled: effectiveMerchantOrganizationUuid !== undefined,
    });
    const enabledGatewaySystemIdsForMerchant: ReadonlyArray<IpgSystemId> = (
        effectiveMerchantOrganizationQuery.data?.enable_ipg_system_ids ?? []
    ).filter((enabledIpgSystemId: IpgSystemId) => allIpgSystemIds.includes(enabledIpgSystemId));
    const isGatewaySelectionAvailable: boolean = effectiveMerchantOrganizationUuid !== undefined;

    useEffect(() => {
        if (enabledGatewaySystemIdsForMerchant.length === 1) {
            const [onlyEnabledGatewaySystemId] = enabledGatewaySystemIdsForMerchant;
            if (onlyEnabledGatewaySystemId === undefined) {
                throw new Error(
                    'Expected exactly one enabled gateway, but none was available'
                );
            }
            setIpgSystemId(onlyEnabledGatewaySystemId);
            return;
        }
        if (
            ipgSystemId !== null &&
            !enabledGatewaySystemIdsForMerchant.includes(ipgSystemId)
        ) {
            setIpgSystemId(null);
        }
    }, [enabledGatewaySystemIdsForMerchant, ipgSystemId]);

    useEffect(() => {
        if (selectedMerchantAccountQuery.data === undefined) {
            return;
        }
        setSelectedMerchantOrganizationUuid(selectedMerchantAccountQuery.data.organization_uuid);
    }, [selectedMerchantAccountQuery.data?.merchant_account_uuid]);

    useEffect(() => {
        if (merchantAccountReference !== null) {
            return;
        }
        setIpgSystemId(null);
    }, [merchantAccountReference]);

    useEffect(() => {
        if (!merchantSelectionLockedBySelectedMerchantAccount) {
            return;
        }
        if (selectedMerchantAccountQuery.data === undefined) {
            return;
        }
        setSelectedMerchantOrganizationUuid(selectedMerchantAccountQuery.data.organization_uuid);
    }, [merchantSelectionLockedBySelectedMerchantAccount, selectedMerchantAccountQuery.data]);

    useEffect(() => {
        if (merchantAccountReference !== null) {
            return;
        }
        setSelectedMerchantOrganizationUuid(null);
    }, [merchantAccountReference]);

    const merchantSelectionResetToken: string = [
        selectedMerchantAccountQuery.data?.merchant_account_uuid ?? 'none',
        merchantSelectionLockedBySelectedMerchantAccount ? 'locked' : 'unlocked',
        selectedMerchantAccountOrganizationQuery.data?.organization_uuid ?? 'org-loading',
    ].join(':');
    const merchantAccountSelectionResetToken: string = [
        shouldRestrictMerchantAccountSearchByMerchantSelection
            ? `merchant:${selectedMerchantOrganizationUuid ?? 'none'}`
            : 'merchant:any',
    ].join(':');

    const fetchMerchantSearchPageForLogicalTerminalCreation = async (params: {
        readonly queryText?: string;
        readonly terms?: ReadonlyArray<string>;
        readonly limit: number;
        readonly cursor?: string;
    }): Promise<{
        readonly items: ReadonlyArray<MerchantOrganizationListItem>;
        readonly pagination: {
            readonly has_more: boolean;
            readonly next_cursor?: string;
        };
    }> => {
        if (selectedMerchantAccountQuery.data === undefined) {
            const merchantSearchResponse = await organizationApi.searchOrganizations({
                ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                limit: params.limit,
                cursor: params.cursor,
                is_merchant: true,
            });
            return {
                items: merchantSearchResponse.organizations.map(
                    (
                        organization: OrganizationOmnisearchListItem
                    ): MerchantOrganizationListItem => ({
                        organization_uuid: organization.organization_uuid,
                        name: organization.name,
                        merchant_accounts: [],
                    })
                ),
                pagination: merchantSearchResponse.pagination,
            };
        }

        if (merchantSelectionLockedBySelectedMerchantAccount) {
            const selectedMerchantAccountOrganization =
                selectedMerchantAccountOrganizationQuery.data;
            if (selectedMerchantAccountOrganization === undefined) {
                return {
                    items: [],
                    pagination: { has_more: false },
                };
            }
            const itemMatchesQueryText: boolean =
                params.queryText === undefined ||
                selectedMerchantAccountOrganization.name
                    .toLowerCase()
                    .includes(params.queryText.toLowerCase());
            return {
                items: itemMatchesQueryText
                    ? [
                          {
                              organization_uuid:
                                  selectedMerchantAccountOrganization.organization_uuid ??
                                  '',
                              name: selectedMerchantAccountOrganization.name,
                              merchant_accounts: [],
                          },
                      ]
                    : [],
                pagination: { has_more: false },
            };
        }

        const merchantSearchResponse = await paymentsInfrastructureApi.searchMerchantOrganizations({
            root_organization_uuid: selectedMerchantAccountQuery.data.organization_uuid,
            ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
            limit: params.limit,
            cursor: params.cursor,
        });
        return {
            items: merchantSearchResponse.merchant_organizations,
            pagination: merchantSearchResponse.pagination,
        };
    };

    const fetchMerchantAccountSearchPageForLogicalTerminalCreation = async (params: {
        readonly queryText?: string;
        readonly terms?: ReadonlyArray<string>;
        readonly limit: number;
        readonly cursor?: string;
    }): Promise<{
        readonly items: ReadonlyArray<MerchantAccountSearchListItem>;
        readonly pagination: {
            readonly has_more: boolean;
            readonly next_cursor?: string;
        };
    }> => {
        const merchantAccountSearchResponse = await paymentsInfrastructureApi.searchMerchantAccounts({
            ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
            limit: params.limit,
            cursor: params.cursor,
            merchant_organization_uuid: shouldRestrictMerchantAccountSearchByMerchantSelection
                ? selectedMerchantOrganizationUuid ?? undefined
                : undefined,
        });
        return {
            items: merchantAccountSearchResponse.merchant_accounts,
            pagination: merchantAccountSearchResponse.pagination,
        };
    };

    const createMutation = useMutation({
        mutationFn: async () => {
            if (selectedMerchantAccountQuery.data === undefined) {
                throw new Error('Merchant account selection is required');
            }
            if (ipgSystemId === null) {
                throw new Error('Gateway selection is required');
            }
            const configurationRequest: LogicalTerminalConfiguration = {};
            const surchargePctValue: number | undefined = toOptionalNumber(surchargePct);
            const convenienceFeeValue: number | undefined = toOptionalNumber(convenienceFee);
            if (allowOverrideFee !== undefined) {
                configurationRequest.allow_override_fee = allowOverrideFee;
            }
            if (surchargePctValue !== undefined) {
                configurationRequest.surcharge_pct = surchargePctValue;
            }
            if (convenienceFeeValue !== undefined) {
                configurationRequest.convenience_fee = convenienceFeeValue;
            }
            const request: CreateLogicalTerminalRequest = {
                merchant_account_uuid: selectedMerchantAccountQuery.data.merchant_account_uuid,
                ipg_system_id: ipgSystemId,
                name: name.trim().length > 0 ? name.trim() : null,
                processor_tid: processorTid.trim(),
                organization_uuid: selectedMerchantOrganizationUuid,
                create_default_physical_terminal_device:
                    defaultTerminalDeviceKind === 'physical',
                create_default_virtual_terminal_device:
                    defaultTerminalDeviceKind === 'virtual',
                logical_terminal_configuration: configurationRequest,
            };
            return await paymentsInfrastructureApi.createLogicalTerminal(request);
        },
        onSuccess: (logicalTerminal: GetLogicalTerminalResponse) => {
            breadcrumbNavigate({
                source: 'terminal',
                target: {
                    url: `/payments-infrastructure/logical-terminals/${logicalTerminal.logical_terminal_uuid}`,
                    title: 'Logical Terminal Details',
                },
            });
        },
    });

    if (activeRoleAssignmentState.isLoading) {
        return <div>Loading...</div>;
    }
    if (!canView || !canCreate) {
        return <div className="text-red-600">Access denied. Insufficient permissions.</div>;
    }

    return (
        <div className="space-y-4">
            <BackButton
                href="/payments-infrastructure/logical-terminals"
                tooltip="Back to Logical Terminals"
            />
            <Card>
                <CardHeader>
                    <CardTitle>Create Logical Terminal</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {merchantSelectionLockedBySelectedMerchantAccount ? (
                        <div>
                            <Label htmlFor="locked_merchant">Merchant</Label>
                            <Input
                                id="locked_merchant"
                                readOnly
                                disabled={selectedMerchantAccountOrganizationQuery.data === undefined}
                                value={selectedMerchantAccountOrganizationQuery.data?.name ?? ''}
                            />
                        </div>
                    ) : (
                        <OmnisearchSelect<MerchantOrganizationListItem, string>
                            label="Merchant"
                            placeholder="Search merchants..."
                            pageSize={50}
                            selectedItemId={selectedMerchantOrganizationUuid}
                            onClearSelection={() => setSelectedMerchantOrganizationUuid(null)}
                            resetToken={merchantSelectionResetToken}
                            fetchPage={fetchMerchantSearchPageForLogicalTerminalCreation}
                            getItemId={(item) => item.organization_uuid}
                            getItemLabel={(item) => item.name}
                            getItemFilterText={(item) =>
                                `${item.name} ${item.organization_uuid}`.toLowerCase()
                            }
                            renderItemCard={renderMerchantOrganizationOmnisearchCard}
                            onSelect={(item) =>
                                setSelectedMerchantOrganizationUuid(item.organization_uuid)
                            }
                        />
                    )}
                    {lockedMerchantAccountUuid === null ? (
                        <OmnisearchSelect<MerchantAccountSearchListItem, string>
                            label="Merchant Account"
                            placeholder="Search merchant accounts..."
                            pageSize={50}
                            selectedItemId={
                                merchantAccountReference ??
                                selectedMerchantAccountQuery.data?.merchant_account_uuid ??
                                null
                            }
                            onClearSelection={() => setMerchantAccountReference(null)}
                            resetToken={merchantAccountSelectionResetToken}
                            fetchPage={fetchMerchantAccountSearchPageForLogicalTerminalCreation}
                            getItemId={(item) => item.merchant_account_uuid}
                            getItemLabel={(item) =>
                                `${item.processor_name} / ${item.processor_mid}`
                            }
                            getItemFilterText={(item) =>
                                `${item.processor_name} ${item.processor_mid} ${item.organization_name} ${item.merchant_account_uuid}`.toLowerCase()
                            }
                            renderItemCard={renderMerchantAccountOmnisearchCard}
                            onSelect={(item) =>
                                setMerchantAccountReference(item.merchant_account_uuid)
                            }
                        />
                    ) : (
                        <div>
                            <Label htmlFor="locked_merchant_account">Merchant Account</Label>
                            <Input
                                id="locked_merchant_account"
                                readOnly
                                disabled
                                value={
                                    selectedMerchantAccountQuery.data === undefined
                                        ? `Merchant Account ${lockedMerchantAccountUuid}`
                                        : `${selectedMerchantAccountQuery.data.processor_name} / ${selectedMerchantAccountQuery.data.processor_mid} (${selectedMerchantAccountQuery.data.merchant_account_uuid})`
                                }
                            />
                        </div>
                    )}
                    {isGatewaySelectionAvailable && (
                        <div>
                            <Label htmlFor="ipg_system_id">Gateway</Label>
                            <select
                                id="ipg_system_id"
                                value={ipgSystemId === null ? '' : String(ipgSystemId)}
                                onChange={(event) => {
                                    const selectedIpgSystemIdFromInput: number = Number(
                                        event.target.value
                                    );
                                    const selectedIpgSystemId: IpgSystemId | undefined =
                                        enabledGatewaySystemIdsForMerchant.find(
                                            (enabledIpgSystemId: IpgSystemId) =>
                                                enabledIpgSystemId === selectedIpgSystemIdFromInput
                                        );
                                    if (selectedIpgSystemId !== undefined) {
                                        setIpgSystemId(selectedIpgSystemId);
                                    } else {
                                        setIpgSystemId(null);
                                    }
                                }}
                                className="w-full border rounded px-3 py-2"
                                disabled={
                                    createMutation.isPending ||
                                    enabledGatewaySystemIdsForMerchant.length === 1
                                }
                            >
                                <option value="">Select Gateway...</option>
                                {enabledGatewaySystemIdsForMerchant.map(
                                    (supportedIpgSystemId: IpgSystemId) => (
                                        <option key={supportedIpgSystemId} value={supportedIpgSystemId}>
                                            {IpgSystemIdToName[supportedIpgSystemId]}
                                        </option>
                                    )
                                )}
                            </select>
                            {enabledGatewaySystemIdsForMerchant.length === 0 && (
                                <p className="text-sm text-destructive mt-1">
                                    No enabled gateways found for this merchant organization.
                                </p>
                            )}
                        </div>
                    )}
                    <div>
                        <Label htmlFor="logical_terminal_name">Name</Label>
                        <Input
                            id="logical_terminal_name"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            placeholder="Optional logical terminal name"
                        />
                    </div>
                    <div>
                        <Label htmlFor="processor_tid">Processor TID</Label>
                        <Input
                            id="processor_tid"
                            value={processorTid}
                            onChange={(event) => setProcessorTid(event.target.value)}
                        />
                    </div>
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="create_default_physical_terminal_device"
                                checked={defaultTerminalDeviceKind === 'physical'}
                                onCheckedChange={(nextValue) => {
                                    setDefaultTerminalDeviceKind(
                                        nextValue === true ? 'physical' : null
                                    );
                                }}
                            />
                            <Label htmlFor="create_default_physical_terminal_device">
                                Create default physical terminal
                            </Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="create_default_virtual_terminal_device"
                                checked={defaultTerminalDeviceKind === 'virtual'}
                                onCheckedChange={(nextValue) => {
                                    setDefaultTerminalDeviceKind(
                                        nextValue === true ? 'virtual' : null
                                    );
                                }}
                            />
                            <Label htmlFor="create_default_virtual_terminal_device">
                                Create default virtual terminal
                            </Label>
                        </div>
                    </div>
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Configuration Overrides (Optional)</CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-2 gap-4">
                            <InheritedCheckbox
                                id="create_logical_allow_override_fee"
                                value={allowOverrideFee}
                                onValueChange={setAllowOverrideFee}
                                label="Allow fee override"
                            />
                            <div>
                                <Label htmlFor="create_logical_surcharge_pct">Surcharge % override</Label>
                                <Input
                                    id="create_logical_surcharge_pct"
                                    value={surchargePct}
                                    onChange={(event) => setSurchargePct(event.target.value)}
                                    placeholder="Override surcharge percentage from merchant account"
                                />
                            </div>
                            <div>
                                <Label htmlFor="create_logical_convenience_fee">
                                    Convenience $ fee override
                                </Label>
                                <Input
                                    id="create_logical_convenience_fee"
                                    value={convenienceFee}
                                    onChange={(event) => setConvenienceFee(event.target.value)}
                                    placeholder="Override convenience fee from merchant account"
                                />
                            </div>
                        </CardContent>
                    </Card>
                    {createMutation.error !== null && (
                        <p className="text-destructive">{String(createMutation.error)}</p>
                    )}
                    {lockedMerchantAccountUuid !== null &&
                        selectedMerchantAccountQuery.error !== null && (
                            <p className="text-destructive">
                                {String(selectedMerchantAccountQuery.error)}
                            </p>
                        )}
                    <Button
                        onClick={() => createMutation.mutate()}
                        disabled={
                            createMutation.isPending ||
                            merchantAccountReference === null ||
                            selectedMerchantAccountQuery.data === undefined ||
                            ipgSystemId === null ||
                            (lockedMerchantAccountUuid !== null &&
                                selectedMerchantAccountQuery.data === undefined) ||
                            processorTid.trim().length === 0
                        }
                    >
                        {createMutation.isPending ? 'Creating...' : 'Create Logical Terminal'}
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
}

export function LogicalTerminalDetailPage(): ReactElement {
    const navigate = useNavigate();
    const params = useParams();
    const logicalTerminalReference: string | undefined = params.logicalTerminalId;
    if (logicalTerminalReference === undefined) {
        throw new Error('Missing logicalTerminalId route parameter');
    }
    const queryClient = useQueryClient();
    const activeRoleAssignmentState = useActiveRoleAssignmentState();
    const canView: boolean = hasPaymentsInfrastructurePage(activeRoleAssignmentState.activeRoleAssignment ?? null);
    const canEdit: boolean = hasInternalAdminPrivileges(activeRoleAssignmentState.activeRoleAssignment ?? null);
    const [isEditingBasicInformation, setIsEditingBasicInformation] = useState<boolean>(false);
    const [draftName, setDraftName] = useState<string>('');
    const [draftProcessorTid, setDraftProcessorTid] = useState<string>('');
    const [draftOrganizationUuid, setDraftOrganizationUuid] = useState<string | null>(null);
    const [draftAppliesToChildren, setDraftAppliesToChildren] = useState<boolean | null>(null);
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);
    const [isOrganizationConflictModalOpen, setIsOrganizationConflictModalOpen] =
        useState<boolean>(false);
    const [organizationConflicts, setOrganizationConflicts] = useState<
        ReadonlyArray<LogicalTerminalOrganizationConflictItem>
    >([]);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [draftConfiguration, setDraftConfiguration] = useState<LogicalTerminalConfiguration>({});
    const [draftSurchargePctText, setDraftSurchargePctText] = useState<string>('');
    const [draftConvenienceFeeText, setDraftConvenienceFeeText] = useState<string>('');
    const [useDefaultTerminalDeviceView, setUseDefaultTerminalDeviceView] =
        useState<boolean>(false);

    useUnsavedChangesRegistration(isEditingBasicInformation);

    const detailQuery = useQuery<GetLogicalTerminalResponse>({
        queryKey: ['paymentsInfrastructure', 'logicalTerminal', logicalTerminalReference],
        queryFn: async () => {
            return await paymentsInfrastructureApi.getLogicalTerminal(logicalTerminalReference);
        },
        enabled: canView,
    });
    const parentMerchantAccountQuery = useQuery<GetMerchantAccountResponse>({
        queryKey: [
            'paymentsInfrastructure',
            'merchantAccount',
            detailQuery.data?.merchant_account_uuid ?? 'none',
        ],
        queryFn: async () => {
            if (detailQuery.data === undefined) {
                throw new Error('Logical terminal details are required');
            }
            return await paymentsInfrastructureApi.getMerchantAccount(
                detailQuery.data.merchant_account_uuid
            );
        },
        enabled: detailQuery.data !== undefined,
    });
    const logicalTerminalTerminalDevicesPreviewQuery = useQuery<
        ReadonlyArray<TerminalDeviceListItem>
    >({
        queryKey: [
            'paymentsInfrastructure',
            'logicalTerminal',
            logicalTerminalReference,
            'terminalDevicesPreview',
        ],
        queryFn: async () => {
            const response = await paymentsInfrastructureApi.searchTerminalDevices({
                logical_terminal_uuid: logicalTerminalReference,
                limit: 2,
            });
            return response.terminal_devices;
        },
        enabled: canView,
    });
    const soleLogicalTerminalDeviceUuid: string | null =
        logicalTerminalTerminalDevicesPreviewQuery.data?.length === 1
            ? logicalTerminalTerminalDevicesPreviewQuery.data[0]?.terminal_device_uuid ?? null
            : null;
    const soleLogicalTerminalDeviceDetailQuery = useQuery<GetTerminalDeviceResponse>({
        queryKey: [
            'paymentsInfrastructure',
            'terminalDevice',
            soleLogicalTerminalDeviceUuid ?? 'none',
        ],
        queryFn: async () => {
            if (soleLogicalTerminalDeviceUuid === null) {
                throw new Error('Sole terminal device UUID is required');
            }
            return await paymentsInfrastructureApi.getTerminalDevice(soleLogicalTerminalDeviceUuid);
        },
        enabled: soleLogicalTerminalDeviceUuid !== null,
    });
    const effectiveLogicalTerminalMerchantOrganizationUuid: string | undefined =
        detailQuery.data?.organization_uuid ?? parentMerchantAccountQuery.data?.organization_uuid;
    const merchantOrganizationQuery = useQuery<GetOrganizationResponse>({
        queryKey: ['organization', effectiveLogicalTerminalMerchantOrganizationUuid ?? ''],
        queryFn: async () => {
            if (effectiveLogicalTerminalMerchantOrganizationUuid === undefined) {
                throw new Error('Logical terminal merchant organization is required');
            }
            return await organizationApi.getOrganization(
                effectiveLogicalTerminalMerchantOrganizationUuid
            );
        },
        enabled: effectiveLogicalTerminalMerchantOrganizationUuid !== undefined,
    });
    useEffect(() => {
        if (detailQuery.data === undefined || isEditing) {
            return;
        }
        setDraftConfiguration(detailQuery.data.logical_terminal_configuration);
        setDraftSurchargePctText(
            toEditableNumberInputValue(detailQuery.data.logical_terminal_configuration.surcharge_pct)
        );
        setDraftConvenienceFeeText(
            toEditableNumberInputValue(detailQuery.data.logical_terminal_configuration.convenience_fee)
        );
    }, [detailQuery.data, isEditing]);
    useEffect(() => {
        if (detailQuery.data === undefined || isEditingBasicInformation) {
            return;
        }
        setDraftName(detailQuery.data.name ?? '');
        setDraftProcessorTid(detailQuery.data.processor_tid);
        setDraftOrganizationUuid(merchantOrganizationQuery.data?.organization_uuid ?? null);
        setDraftAppliesToChildren(detailQuery.data.applies_to_children);
    }, [detailQuery.data, isEditingBasicInformation]);
    const previewTerminalDevices: ReadonlyArray<TerminalDeviceListItem> =
        logicalTerminalTerminalDevicesPreviewQuery.data ?? [];
    const soleTerminalDevice: TerminalDeviceListItem | null =
        previewTerminalDevices.length === 1 ? previewTerminalDevices[0] ?? null : null;
    const shouldOfferDefaultTerminalDeviceView: boolean =
        soleLogicalTerminalDeviceDetailQuery.data?.credential_uuid === null &&
        isEmptyTerminalDeviceConfiguration(
            soleLogicalTerminalDeviceDetailQuery.data?.terminal_device_configuration
        );
    useEffect(() => {
        setUseDefaultTerminalDeviceView(shouldOfferDefaultTerminalDeviceView);
    }, [logicalTerminalReference, shouldOfferDefaultTerminalDeviceView]);

    const updateMutation = useMutation({
        mutationFn: async () => {
            const surchargePctNumber: number | undefined = toOptionalNumber(draftSurchargePctText);
            const convenienceFeeNumber: number | undefined = toOptionalNumber(draftConvenienceFeeText);
            if (
                (surchargePctNumber !== undefined && !Number.isFinite(surchargePctNumber)) ||
                (convenienceFeeNumber !== undefined && !Number.isFinite(convenienceFeeNumber))
            ) {
                throw new Error('Surcharge % and convenience fee overrides must be valid numbers');
            }
            return await paymentsInfrastructureApi.updateLogicalTerminal(logicalTerminalReference, {
                logical_terminal_configuration: {
                    ...draftConfiguration,
                    surcharge_pct: surchargePctNumber,
                    convenience_fee: convenienceFeeNumber,
                },
            });
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['paymentsInfrastructure', 'logicalTerminal', logicalTerminalReference],
            });
            setIsEditing(false);
        },
    });
    const updateBasicInformationMutation = useMutation({
        mutationFn: async (clearConflictingTerminalDeviceOrganizationIds: boolean) => {
            return await paymentsInfrastructureApi.updateLogicalTerminal(logicalTerminalReference, {
                name: draftName.trim().length > 0 ? draftName.trim() : null,
                processor_tid: draftProcessorTid.trim(),
                organization_uuid: draftOrganizationUuid,
                applies_to_children: draftAppliesToChildren,
                clear_conflicting_terminal_device_organization_ids:
                    clearConflictingTerminalDeviceOrganizationIds,
            });
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['paymentsInfrastructure', 'logicalTerminal', logicalTerminalReference],
            });
            setIsEditingBasicInformation(false);
        },
    });
    const previewOrganizationChangeMutation = useMutation({
        mutationFn: async (nextOrganizationId: string | null) => {
            return await paymentsInfrastructureApi.previewLogicalTerminalOrganizationChange(
                logicalTerminalReference,
                {
                    organization_uuid: nextOrganizationId,
                }
            );
        },
    });
    const deleteMutation = useMutation({
        mutationFn: async () => {
            return await paymentsInfrastructureApi.deleteLogicalTerminal(logicalTerminalReference);
        },
        onSuccess: () => {
            runInBackground(navigate('/payments-infrastructure/logical-terminals'), (error: unknown) => {
                throw error;
            });
        },
    });
    const updateSecretAssociationMutation = useMutation({
        mutationFn: async (secretIdentity: Pick<SecretListItem, 'credential_uuid'> | null) => {
            return await paymentsInfrastructureApi.updateLogicalTerminal(logicalTerminalReference, {
                credential_uuid: secretIdentity?.credential_uuid ?? null,
            });
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['paymentsInfrastructure', 'logicalTerminal', logicalTerminalReference],
            });
        },
    });
    const undeleteMutation = useMutation({
        mutationFn: async () => {
            return await paymentsInfrastructureApi.updateLogicalTerminal(logicalTerminalReference, {
                status: Status.ACTIVE,
            });
        },
        onSuccess: async (updatedLogicalTerminal: GetLogicalTerminalResponse) => {
            queryClient.setQueryData(
                ['paymentsInfrastructure', 'logicalTerminal', logicalTerminalReference],
                updatedLogicalTerminal
            );
            await queryClient.invalidateQueries({
                queryKey: ['paymentsInfrastructure', 'logicalTerminals'],
            });
        },
    });
    if (activeRoleAssignmentState.isLoading || detailQuery.isLoading) {
        return <div>Loading...</div>;
    }
    if (!canView) {
        return <div className="text-red-600">Access denied. Insufficient permissions.</div>;
    }
    if (detailQuery.data === undefined) {
        return <div>Logical terminal not found.</div>;
    }

    const logicalTerminal: GetLogicalTerminalResponse = detailQuery.data;
    const logicalTerminalAttachedSecretIdentity: Pick<SecretListItem, 'credential_uuid'> | null =
        logicalTerminal.credential_uuid === null
            ? null
            : {
                  credential_uuid: logicalTerminal.credential_uuid,
              };
    const showLogicalTerminalCredentialsCard: boolean =
        shouldOfferDefaultTerminalDeviceView &&
        (useDefaultTerminalDeviceView || logicalTerminalAttachedSecretIdentity !== null);
    const showTerminalDevicesCard: boolean =
        !shouldOfferDefaultTerminalDeviceView || !useDefaultTerminalDeviceView;
    const editingSurchargePct: number | undefined = toOptionalNumber(draftSurchargePctText);
    const editingConvenienceFee: number | undefined = toOptionalNumber(draftConvenienceFeeText);
    const effectiveDraftSurchargePct: number | undefined =
        isEditing && Number.isFinite(editingSurchargePct ?? Number.NaN)
            ? editingSurchargePct
            : draftConfiguration.surcharge_pct;
    const effectiveDraftConvenienceFee: number | undefined =
        isEditing && Number.isFinite(editingConvenienceFee ?? Number.NaN)
            ? editingConvenienceFee
            : draftConfiguration.convenience_fee;
    const effectiveSurchargePct: number | undefined =
        effectiveDraftSurchargePct ??
        parentMerchantAccountQuery.data?.merchant_account_configuration.surcharge_pct;
    const effectiveConvenienceFee: number | undefined =
        effectiveDraftConvenienceFee ??
        parentMerchantAccountQuery.data?.merchant_account_configuration.convenience_fee;
    const effectiveAllowOverrideFee: boolean =
        draftConfiguration.allow_override_fee ??
        parentMerchantAccountQuery.data?.merchant_account_configuration.allow_override_fee ??
        false;
    const effectiveAppliesToChildren: boolean | undefined =
        logicalTerminal.applies_to_children ?? parentMerchantAccountQuery.data?.applies_to_children;
    const handleSaveBasicInformation = () => {
        const hasOrganizationChanged: boolean =
            draftOrganizationUuid !== (merchantOrganizationQuery.data?.organization_uuid ?? null);
        if (!hasOrganizationChanged) {
            updateBasicInformationMutation.mutate(false);
            return;
        }
        runInBackground(
            (async () => {
                const preview =
                    await previewOrganizationChangeMutation.mutateAsync(draftOrganizationUuid);
                if (preview.conflicts.length === 0) {
                    updateBasicInformationMutation.mutate(false);
                    return;
                }
                setOrganizationConflicts(preview.conflicts);
                setIsOrganizationConflictModalOpen(true);
            })(),
            (error: unknown) => {
                throw error;
            }
        );
    };
    return (
        <div className="space-y-4">
            <BackButton
                href="/payments-infrastructure/logical-terminals"
                tooltip="Back to Logical Terminals"
            />
            <div className="mb-8 flex items-center gap-3">
                <h1 className="text-3xl font-bold">Logical Terminal Details</h1>
                <StatusBadge status={logicalTerminal.status} />
            </div>
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Basic Information</CardTitle>
                    {canEdit && !isEditingBasicInformation && (
                        <div className="flex gap-2">
                            {logicalTerminal.status !== Status.DELETED ? (
                                <Button size="sm" onClick={() => setIsEditingBasicInformation(true)}>
                                    Edit
                                </Button>
                            ) : null}
                            {logicalTerminal.status === Status.DELETED ? (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => undeleteMutation.mutate()}
                                    disabled={undeleteMutation.isPending || deleteMutation.isPending}
                                >
                                    Undelete
                                </Button>
                            ) : (
                                <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => setIsDeleteConfirmationOpen(true)}
                                    disabled={deleteMutation.isPending || undeleteMutation.isPending}
                                >
                                    Delete
                                </Button>
                            )}
                        </div>
                    )}
                    {canEdit && isEditingBasicInformation && (
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                onClick={handleSaveBasicInformation}
                                disabled={
                                    updateBasicInformationMutation.isPending ||
                                    draftProcessorTid.trim().length === 0
                                }
                            >
                                {updateBasicInformationMutation.isPending ? 'Saving...' : 'Save'}
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                    setDraftName(logicalTerminal.name ?? '');
                                    setDraftProcessorTid(logicalTerminal.processor_tid);
                                    setDraftOrganizationUuid(
                                        merchantOrganizationQuery.data?.organization_uuid ?? null
                                    );
                                    setDraftAppliesToChildren(logicalTerminal.applies_to_children);
                                    setIsEditingBasicInformation(false);
                                }}
                                disabled={updateBasicInformationMutation.isPending}
                            >
                                Cancel
                            </Button>
                        </div>
                    )}
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                    <div>
                        <p className="text-sm text-muted-foreground">Merchant Account</p>
                        <p
                            className="font-semibold text-primary underline cursor-pointer"
                            onClick={() => {
                                runInBackground(
                                    navigate(
                                        `/payments-infrastructure/merchant-accounts/${logicalTerminal.merchant_account_uuid}`
                                    ),
                                    (error: unknown) => {
                                        throw error;
                                    }
                                );
                            }}
                        >
                            {`${logicalTerminal.processor_mid} - ${logicalTerminal.processor_name}`}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Merchant</p>
                        {isEditingBasicInformation ? (
                            parentMerchantAccountQuery.data === undefined ? (
                                <p className="font-semibold text-muted-foreground">Loading...</p>
                            ) : (
                                <div className="space-y-2">
                                    <OmnisearchSelect<MerchantOrganizationListItem, string>
                                        label="Merchant"
                                        placeholder="Search merchants..."
                                        pageSize={50}
                                        selectedItemId={draftOrganizationUuid}
                                        onClearSelection={() => setDraftOrganizationUuid(null)}
                                        fetchPage={async (params) => {
                                            const response =
                                                await paymentsInfrastructureApi.searchMerchantOrganizations(
                                                    {
                                                        root_organization_uuid:
                                                            parentMerchantAccountQuery.data
                                                                .organization_uuid,
                                                        ...(params.terms !== undefined
                                                            ? { terms: [...params.terms] }
                                                            : {}),
                                                        limit: params.limit,
                                                        cursor: params.cursor,
                                                    }
                                                );
                                            return {
                                                items: response.merchant_organizations,
                                                pagination: response.pagination,
                                            };
                                        }}
                                        getItemId={(item) => item.organization_uuid}
                                        getItemLabel={(item) => item.name}
                                        getItemFilterText={(item) =>
                                            `${item.name} ${item.organization_uuid}`.toLowerCase()
                                        }
                                        renderItemCard={renderMerchantOrganizationOmnisearchCard}
                                        onSelect={(item) =>
                                            setDraftOrganizationUuid(item.organization_uuid)
                                        }
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setDraftOrganizationUuid(null)}
                                    >
                                        Use inherited merchant
                                    </Button>
                                </div>
                            )
                        ) : merchantOrganizationQuery.data === undefined ? (
                            <p className="font-semibold text-muted-foreground">Loading...</p>
                        ) : (
                            <p
                                className="font-semibold text-primary underline cursor-pointer"
                                onClick={() => {
                                    runInBackground(
                                        navigate(
                                            `/organizations/${merchantOrganizationQuery.data.organization_uuid}`
                                        ),
                                        (error: unknown) => {
                                            throw error;
                                        }
                                    );
                                }}
                            >
                                {merchantOrganizationQuery.data.name}
                            </p>
                        )}
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Name</p>
                        {isEditingBasicInformation ? (
                            <Input
                                value={draftName}
                                onChange={(event) => setDraftName(event.target.value)}
                                placeholder="Optional logical terminal name"
                            />
                        ) : (
                            <p className="font-semibold">
                                {logicalTerminal.name ?? '—'}
                            </p>
                        )}
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Processor Name</p>
                        <p className="font-semibold">{logicalTerminal.processor_name}</p>
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Gateway</p>
                        <p className="font-semibold">{IpgSystemIdToName[logicalTerminal.ipg_system_id]}</p>
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Processor MID</p>
                        <p className="font-semibold">{logicalTerminal.processor_mid}</p>
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Processor TID</p>
                        {isEditingBasicInformation ? (
                            <Input
                                value={draftProcessorTid}
                                onChange={(event) => setDraftProcessorTid(event.target.value)}
                            />
                        ) : (
                            <p className="font-semibold">{logicalTerminal.processor_tid}</p>
                        )}
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Applies to children</p>
                        {isEditingBasicInformation ? (
                            <div className="space-y-2 pt-2">
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="logical_terminal_applies_to_children"
                                        checked={draftAppliesToChildren === true}
                                        onCheckedChange={(nextValue) =>
                                            setDraftAppliesToChildren(nextValue === true)
                                        }
                                    />
                                    <Label htmlFor="logical_terminal_applies_to_children">
                                        Enabled
                                    </Label>
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setDraftAppliesToChildren(null)}
                                >
                                    Use inherited value
                                </Button>
                            </div>
                        ) : logicalTerminal.applies_to_children === null ? (
                            <p className="font-semibold">
                                {`Inherited (${effectiveAppliesToChildren === true ? 'Yes' : 'No'})`}
                            </p>
                        ) : (
                            <p className="font-semibold">
                                {logicalTerminal.applies_to_children ? 'Yes' : 'No'}
                            </p>
                        )}
                    </div>
                    {updateBasicInformationMutation.error !== null && (
                        <p className="text-destructive col-span-2">
                            {String(updateBasicInformationMutation.error)}
                        </p>
                    )}
                </CardContent>
            </Card>
            <ConfirmationModal
                open={isDeleteConfirmationOpen}
                onOpenChange={setIsDeleteConfirmationOpen}
                message="Delete this logical terminal? This operation marks the status as DELETED."
                onConfirm={() => {
                    deleteMutation.mutate();
                }}
                onCancel={() => {
                    setIsDeleteConfirmationOpen(false);
                }}
                confirmLabel="Delete"
                isLoading={deleteMutation.isPending}
            />
            <Dialog
                open={isOrganizationConflictModalOpen}
                onOpenChange={setIsOrganizationConflictModalOpen}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm merchant change</DialogTitle>
                        <DialogDescription>
                            Some terminal devices have local merchants outside the target subtree. Confirm
                            to clear those local merchant assignments before saving.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="max-h-64 overflow-y-auto space-y-2">
                        {organizationConflicts.map(
                            (conflict: LogicalTerminalOrganizationConflictItem) => (
                                <div
                                    key={conflict.terminal_device_uuid}
                                    className="rounded border px-3 py-2 text-sm"
                                >
                                    <div className="font-medium">
                                        Terminal {conflict.processor_device_id}
                                    </div>
                                    <div className="text-muted-foreground">
                                        Merchant: {conflict.organization_name}
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setIsOrganizationConflictModalOpen(false)}
                            disabled={updateBasicInformationMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={() => {
                                setIsOrganizationConflictModalOpen(false);
                                updateBasicInformationMutation.mutate(true);
                            }}
                            disabled={updateBasicInformationMutation.isPending}
                        >
                            Confirm
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ConfigurationEditorShell
                title="Configuration"
                canEdit={canEdit}
                isEditing={isEditing}
                onEdit={() => setIsEditing(true)}
                onCancel={() => {
                    setDraftConfiguration(logicalTerminal.logical_terminal_configuration);
                    setDraftSurchargePctText(
                        toEditableNumberInputValue(
                            logicalTerminal.logical_terminal_configuration.surcharge_pct
                        )
                    );
                    setDraftConvenienceFeeText(
                        toEditableNumberInputValue(
                            logicalTerminal.logical_terminal_configuration.convenience_fee
                        )
                    );
                    setIsEditing(false);
                }}
                onSave={() => updateMutation.mutate()}
                isSaving={updateMutation.isPending}
            >
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <InheritedCheckbox
                            id="detail_logical_allow_override_fee"
                            value={draftConfiguration.allow_override_fee}
                            disabled={!isEditing}
                            onValueChange={(nextValue) =>
                                setDraftConfiguration((currentValue: LogicalTerminalConfiguration) => ({
                                    ...currentValue,
                                    allow_override_fee: nextValue,
                                }))
                            }
                            label="Allow fee override"
                        />
                        <div>
                            <Label htmlFor="detail_logical_surcharge_pct">Surcharge % override</Label>
                            <Input
                                id="detail_logical_surcharge_pct"
                                disabled={!isEditing}
                                value={draftSurchargePctText}
                                onChange={(event) => setDraftSurchargePctText(event.target.value)}
                            />
                        </div>
                        <div>
                            <Label htmlFor="detail_logical_convenience_fee">
                                Convenience fee override
                            </Label>
                            <Input
                                id="detail_logical_convenience_fee"
                                disabled={!isEditing}
                                value={draftConvenienceFeeText}
                                onChange={(event) => setDraftConvenienceFeeText(event.target.value)}
                            />
                        </div>
                    </div>
                    <div className="border-t pt-4 grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-sm text-muted-foreground">Effective allow fee override</p>
                            <p className="font-semibold">{effectiveAllowOverrideFee ? 'Yes' : 'No'}</p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">Effective surcharge %</p>
                            <p className="font-semibold">
                                {effectiveSurchargePct === undefined ? 'undefined' : effectiveSurchargePct}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">Effective convenience fee</p>
                            <p className="font-semibold">
                                {effectiveConvenienceFee === undefined
                                    ? 'undefined'
                                    : effectiveConvenienceFee}
                            </p>
                        </div>
                    </div>
                </div>
            </ConfigurationEditorShell>
            {shouldOfferDefaultTerminalDeviceView && (
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="use_default_terminal_device"
                        checked={useDefaultTerminalDeviceView}
                        onCheckedChange={(nextValue) =>
                            setUseDefaultTerminalDeviceView(nextValue === true)
                        }
                    />
                    <Label htmlFor="use_default_terminal_device">
                        Use default terminal device
                    </Label>
                </div>
            )}
            {showTerminalDevicesCard && (
                <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Terminal Devices</CardTitle>
                    {canEdit && logicalTerminal.status === Status.ACTIVE && (
                        <Button
                            size="sm"
                            onClick={() => {
                                    runInBackground(
                                        navigate(
                                            `/payments-infrastructure/terminal-devices/create?logical_terminal_uuid=${logicalTerminal.logical_terminal_uuid}`
                                        ),
                                        (error: unknown) => {
                                            throw error;
                                    }
                                );
                            }}
                        >
                            Create Terminal Device
                        </Button>
                    )}
                </CardHeader>
                <CardContent>
                    <EmbeddedInfiniteScrollList<TerminalDeviceListItem, string, readonly unknown[]>
                        queryKeyPrefix={[
                            'paymentsInfrastructure',
                            'logicalTerminal',
                            logicalTerminalReference,
                            'terminalDevices',
                        ]}
                        pageSize={50}
                        maxHeight="50rem"
                        enabled={canView}
                        parseCursorParam={parseOpaqueEmbeddedListCursorParam}
                        fetchPage={async ({ limit, cursor }) => {
                            const response = await paymentsInfrastructureApi.searchTerminalDevices({
                                logical_terminal_uuid: logicalTerminal.logical_terminal_uuid,
                                limit,
                                cursor,
                            });
                            return {
                                items: response.terminal_devices,
                                pagination: response.pagination,
                            };
                        }}
                        stateCopy={{
                            emptyLabel: 'No terminal devices configured for this logical terminal.',
                        }}
                        renderItems={({ items }) => (
                            <div className="space-y-2">
                                {items.map((terminalDevice: TerminalDeviceListItem) => (
                                    <div
                                        key={terminalDevice.terminal_device_uuid}
                                        className="border rounded p-3 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                                        onClick={() => {
                                            runInBackground(
                                                navigate(
                                                    `/payments-infrastructure/terminal-devices/${terminalDevice.terminal_device_uuid}`
                                                ),
                                                (error: unknown) => {
                                                    throw error;
                                                }
                                            );
                                        }}
                                    >
                                        <div>
                                            <div className="font-medium flex items-center gap-2">
                                                {getTerminalDeviceDisplayLabel(terminalDevice)}
                                                <span
                                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                                        terminalDevice.is_virtual
                                                            ? 'bg-gray-100 text-gray-700'
                                                            : 'bg-blue-100 text-blue-700'
                                                    }`}
                                                >
                                                    {terminalDevice.is_virtual ? 'Virtual' : 'Physical'}
                                                </span>
                                            </div>
                                        </div>
                                        <StatusBadge status={terminalDevice.status} />
                                    </div>
                                ))}
                            </div>
                        )}
                    />
                </CardContent>
                </Card>
            )}
            {showLogicalTerminalCredentialsCard &&
                soleTerminalDevice !== null &&
                parentMerchantAccountQuery.data !== undefined &&
                merchantOrganizationQuery.data !== undefined &&
                merchantOrganizationQuery.data.organization_uuid !== undefined && (
                    <SecretAssociationCard
                        cardTitle="Credentials"
                        entityLabelForDialog="logical terminal"
                        organizationUuid={merchantOrganizationQuery.data.organization_uuid}
                        organizationName={`${parentMerchantAccountQuery.data.processor_name} / ${parentMerchantAccountQuery.data.processor_mid}`}
                        requiredCredentialType={
                            soleTerminalDevice.is_virtual
                                ? 'VIRTUAL_TERMINAL'
                                : 'PHYSICAL_TERMINAL'
                        }
                        credentialTargetFamily="ipg"
                        credentialSystemSelectLabel="Integrated Payments Gateway"
                        availableCredentialSystemIds={SupportedIpgSystemIds}
                        attachedSecretIdentity={logicalTerminalAttachedSecretIdentity}
                        canEdit={canEdit}
                        onAttachSecret={(secretIdentity) => {
                            updateSecretAssociationMutation.mutate(secretIdentity);
                        }}
                        onDetachSecret={() => {
                            updateSecretAssociationMutation.mutate(null);
                        }}
                        onAssociationChanged={() =>
                            queryClient.invalidateQueries({
                                queryKey: [
                                    'paymentsInfrastructure',
                                    'logicalTerminal',
                                    logicalTerminalReference,
                                ],
                            })
                        }
                        isMutating={updateSecretAssociationMutation.isPending}
                    />
                )}
            {updateSecretAssociationMutation.error !== null && (
                <p className="text-destructive">
                    {String(updateSecretAssociationMutation.error)}
                </p>
            )}
        </div>
    );
}

export function CreateTerminalDevicePage(): ReactElement {
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const [searchParams] = useSearchParams();
    const lockedLogicalTerminalUuidRaw: string | null = searchParams.get('logical_terminal_uuid');
    const lockedLogicalTerminalUuid: string | null =
        lockedLogicalTerminalUuidRaw === null
            ? null
            : LogicalTerminalUuidSearchParamSchema.parse(lockedLogicalTerminalUuidRaw);
    const activeRoleAssignmentState = useActiveRoleAssignmentState();
    const canView: boolean = hasPaymentsInfrastructurePage(activeRoleAssignmentState.activeRoleAssignment ?? null);
    const canCreate: boolean = hasInternalAdminPrivileges(activeRoleAssignmentState.activeRoleAssignment ?? null);
    const [logicalTerminalUuid, setLogicalTerminalUuid] = useState<string | null>(
        lockedLogicalTerminalUuid
    );
    const [organizationUuid, setOrganizationUuid] = useState<string | null>(null);
    const [processorDeviceId, setProcessorDeviceId] = useState<string>('');
    const [isVirtual, setIsVirtual] = useState<boolean>(false);
    const [surchargePct, setSurchargePct] = useState<string>('');
    const [convenienceFee, setConvenienceFee] = useState<string>('');
    const [allowOverrideFee, setAllowOverrideFee] = useState<boolean | undefined>(undefined);
    const selectedLogicalTerminalQuery = useQuery<GetLogicalTerminalResponse>({
        queryKey: ['paymentsInfrastructure', 'logicalTerminal', logicalTerminalUuid],
        queryFn: async () => {
            if (logicalTerminalUuid === null) {
                throw new Error('Logical terminal must be selected');
            }
            return await paymentsInfrastructureApi.getLogicalTerminal(logicalTerminalUuid);
        },
        enabled: logicalTerminalUuid !== null,
    });
    const selectedLogicalTerminalMerchantAccountQuery = useQuery<GetMerchantAccountResponse>({
        queryKey: [
            'paymentsInfrastructure',
            'merchantAccount',
            selectedLogicalTerminalQuery.data?.merchant_account_uuid ?? 'none',
        ],
        queryFn: async () => {
            if (selectedLogicalTerminalQuery.data === undefined) {
                throw new Error('Logical terminal must be selected');
            }
            return await paymentsInfrastructureApi.getMerchantAccount(
                selectedLogicalTerminalQuery.data.merchant_account_uuid
            );
        },
        enabled: selectedLogicalTerminalQuery.data !== undefined,
    });
    useEffect(() => {
        setOrganizationUuid(null);
    }, [logicalTerminalUuid]);

    const createMutation = useMutation({
        mutationFn: async () => {
            if (logicalTerminalUuid === null || selectedLogicalTerminalQuery.data === undefined) {
                throw new Error('Logical terminal selection is required');
            }
            const configurationRequest: TerminalDeviceConfiguration = {};
            const surchargePctValue: number | undefined = toOptionalNumber(surchargePct);
            const convenienceFeeValue: number | undefined = toOptionalNumber(convenienceFee);
            if (surchargePctValue !== undefined) {
                configurationRequest.surcharge_pct = surchargePctValue;
            }
            if (convenienceFeeValue !== undefined) {
                configurationRequest.convenience_fee = convenienceFeeValue;
            }
            if (allowOverrideFee !== undefined) {
                configurationRequest.allow_override_fee = allowOverrideFee;
            }
            const request: CreateTerminalDeviceRequest = {
                logical_terminal_uuid: selectedLogicalTerminalQuery.data.logical_terminal_uuid,
                processor_device_id: processorDeviceId.trim(),
                is_virtual: isVirtual,
                organization_uuid: organizationUuid,
                terminal_device_configuration: configurationRequest,
            };
            return await paymentsInfrastructureApi.createTerminalDevice(request);
        },
        onSuccess: (terminalDevice: GetTerminalDeviceResponse) => {
            breadcrumbNavigate({
                source: 'terminal',
                target: {
                    url: `/payments-infrastructure/terminal-devices/${terminalDevice.terminal_device_uuid}`,
                    title: 'Terminal Device Details',
                },
            });
        },
    });

    if (activeRoleAssignmentState.isLoading) {
        return <div>Loading...</div>;
    }
    if (!canView || !canCreate) {
        return <div className="text-red-600">Access denied. Insufficient permissions.</div>;
    }

    return (
        <div className="space-y-4">
            <BackButton
                href="/payments-infrastructure/terminal-devices"
                tooltip="Back to Terminal Devices"
            />
            <Card>
                <CardHeader>
                    <CardTitle>Create Terminal Device</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {lockedLogicalTerminalUuid === null ? (
                        <OmnisearchSelect<LogicalTerminalListItem, string>
                            label="Logical Terminal"
                            placeholder="Search logical terminals..."
                            pageSize={50}
                            selectedItemId={logicalTerminalUuid}
                            onClearSelection={() => setLogicalTerminalUuid(null)}
                            fetchPage={async (params) => {
                                const response = await paymentsInfrastructureApi.searchLogicalTerminals({
                                    ...(params.terms !== undefined
                                        ? { terms: [...params.terms] }
                                        : {}),
                                    limit: params.limit,
                                    cursor: params.cursor,
                                });
                                return {
                                    items: response.logical_terminals,
                                    pagination: response.pagination,
                                };
                            }}
                            getItemId={(item) => item.logical_terminal_uuid}
                            getItemLabel={getLogicalTerminalDisplayLabel}
                            getItemFilterText={(item) =>
                                `${item.name ?? ''} ${item.processor_name} ${item.processor_mid} ${item.processor_tid}`.toLowerCase()
                            }
                            onSelect={(item) => setLogicalTerminalUuid(item.logical_terminal_uuid)}
                        />
                    ) : (
                        <div>
                            <Label htmlFor="locked_logical_terminal">Logical Terminal</Label>
                            <Input
                                id="locked_logical_terminal"
                                readOnly
                                disabled
                                value={
                                    selectedLogicalTerminalQuery.data === undefined
                                        ? `Logical Terminal ${lockedLogicalTerminalUuid}`
                                        : getLogicalTerminalDisplayLabel(
                                              selectedLogicalTerminalQuery.data
                                          )
                                }
                            />
                        </div>
                    )}
                    {selectedLogicalTerminalQuery.data !== undefined &&
                        selectedLogicalTerminalMerchantAccountQuery.data !== undefined && (
                            <div className="space-y-2">
                                <OmnisearchSelect<MerchantOrganizationListItem, string>
                                    label="Merchant"
                                    placeholder="Search merchants..."
                                    pageSize={50}
                                    selectedItemId={organizationUuid}
                                    onClearSelection={() => setOrganizationUuid(null)}
                                    fetchPage={async (params) => {
                                        const rootOrganizationId: string =
                                            selectedLogicalTerminalMerchantAccountQuery.data
                                                .organization_uuid;
                                        const response =
                                            await paymentsInfrastructureApi.searchMerchantOrganizations(
                                                {
                                                    root_organization_uuid: rootOrganizationId,
                                                    ...(params.terms !== undefined
                                                        ? { terms: [...params.terms] }
                                                        : {}),
                                                    limit: params.limit,
                                                    cursor: params.cursor,
                                                }
                                            );
                                        return {
                                            items: response.merchant_organizations,
                                            pagination: response.pagination,
                                        };
                                    }}
                                    getItemId={(item) => item.organization_uuid}
                                    getItemLabel={(item) => item.name}
                                    getItemFilterText={(item) =>
                                        `${item.name} ${item.organization_uuid}`.toLowerCase()
                                    }
                                    renderItemCard={renderMerchantOrganizationOmnisearchCard}
                                    onSelect={(item) => setOrganizationUuid(item.organization_uuid)}
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setOrganizationUuid(null)}
                                >
                                    Use inherited merchant
                                </Button>
                            </div>
                        )}
                    <div>
                        <Label htmlFor="create_terminal_device_id">Processor Device ID</Label>
                        <Input
                            id="create_terminal_device_id"
                            value={processorDeviceId}
                            onChange={(event) => setProcessorDeviceId(event.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="create_terminal_device_is_virtual"
                            checked={isVirtual}
                            onCheckedChange={(nextValue) => setIsVirtual(nextValue === true)}
                        />
                        <Label htmlFor="create_terminal_device_is_virtual">Virtual terminal</Label>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="create_terminal_device_surcharge_pct">
                                Surcharge % override
                            </Label>
                            <Input
                                id="create_terminal_device_surcharge_pct"
                                value={surchargePct}
                                onChange={(event) => setSurchargePct(event.target.value)}
                            />
                        </div>
                        <div>
                            <Label htmlFor="create_terminal_device_convenience_fee">
                                Convenience fee override
                            </Label>
                            <Input
                                id="create_terminal_device_convenience_fee"
                                value={convenienceFee}
                                onChange={(event) => setConvenienceFee(event.target.value)}
                            />
                        </div>
                    </div>
                    <InheritedCheckbox
                        id="create_terminal_device_allow_override_fee"
                        value={allowOverrideFee}
                        onValueChange={setAllowOverrideFee}
                        label="Allow fee override"
                    />
                    {createMutation.error !== null && (
                        <p className="text-destructive">{String(createMutation.error)}</p>
                    )}
                    {lockedLogicalTerminalUuid !== null &&
                        selectedLogicalTerminalQuery.error !== null && (
                            <p className="text-destructive">
                                {String(selectedLogicalTerminalQuery.error)}
                            </p>
                        )}
                    <Button
                        onClick={() => createMutation.mutate()}
                        disabled={
                            createMutation.isPending ||
                            logicalTerminalUuid === null ||
                            (lockedLogicalTerminalUuid !== null &&
                                selectedLogicalTerminalQuery.data === undefined) ||
                            processorDeviceId.trim().length === 0
                        }
                    >
                        {createMutation.isPending ? 'Creating...' : 'Create Terminal Device'}
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
}

export function TerminalDeviceDetailPage(): ReactElement {
    const navigate = useNavigate();
    const params = useParams();
    const terminalDeviceReference: string | undefined = params.terminalDeviceId;
    if (terminalDeviceReference === undefined) {
        throw new Error('Missing terminalDeviceId route parameter');
    }
    const queryClient = useQueryClient();
    const activeRoleAssignmentState = useActiveRoleAssignmentState();
    const canView: boolean = hasPaymentsInfrastructurePage(activeRoleAssignmentState.activeRoleAssignment ?? null);
    const canEdit: boolean = hasInternalAdminPrivileges(activeRoleAssignmentState.activeRoleAssignment ?? null);
    const [isEditingBasicInformation, setIsEditingBasicInformation] = useState<boolean>(false);
    const [draftProcessorDeviceId, setDraftProcessorDeviceId] = useState<string>('');
    const [draftName, setDraftName] = useState<string>('');
    const [draftIsVirtual, setDraftIsVirtual] = useState<boolean>(false);
    const [draftOrganizationId, setDraftOrganizationId] = useState<string | null>(null);
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [draftConfiguration, setDraftConfiguration] = useState<TerminalDeviceConfiguration>({});
    const [draftSurchargePctText, setDraftSurchargePctText] = useState<string>('');
    const [draftConvenienceFeeText, setDraftConvenienceFeeText] = useState<string>('');

    useUnsavedChangesRegistration(isEditingBasicInformation);

    const detailQuery = useQuery<GetTerminalDeviceResponse>({
        queryKey: ['paymentsInfrastructure', 'terminalDevice', terminalDeviceReference],
        queryFn: async () => {
            return await paymentsInfrastructureApi.getTerminalDevice(terminalDeviceReference);
        },
        enabled: canView,
    });

    const parentLogicalTerminalQuery = useQuery<GetLogicalTerminalResponse>({
        queryKey: [
            'paymentsInfrastructure',
            'logicalTerminal',
            detailQuery.data?.logical_terminal_uuid ?? 'none',
        ],
        queryFn: async () => {
            if (detailQuery.data === undefined) {
                throw new Error('Terminal device details are required');
            }
            return await paymentsInfrastructureApi.getLogicalTerminal(
                detailQuery.data.logical_terminal_uuid
            );
        },
        enabled: detailQuery.data !== undefined,
    });

    const parentMerchantAccountQuery = useQuery<GetMerchantAccountResponse>({
        queryKey: [
            'paymentsInfrastructure',
            'merchantAccount',
            parentLogicalTerminalQuery.data?.merchant_account_uuid ?? 'none',
        ],
        queryFn: async () => {
            if (parentLogicalTerminalQuery.data === undefined) {
                throw new Error('Logical terminal details are required');
            }
            return await paymentsInfrastructureApi.getMerchantAccount(
                parentLogicalTerminalQuery.data.merchant_account_uuid
            );
        },
        enabled: parentLogicalTerminalQuery.data !== undefined,
    });
    const effectiveTerminalDeviceMerchantOrganizationUuid: string | undefined =
        detailQuery.data?.organization_uuid ??
        parentLogicalTerminalQuery.data?.organization_uuid ??
        parentMerchantAccountQuery.data?.organization_uuid;
    const merchantOrganizationQuery = useQuery<GetOrganizationResponse>({
        queryKey: ['organization', effectiveTerminalDeviceMerchantOrganizationUuid ?? ''],
        queryFn: async () => {
            if (effectiveTerminalDeviceMerchantOrganizationUuid === undefined) {
                throw new Error('Terminal device merchant organization is required');
            }
            return await organizationApi.getOrganization(
                effectiveTerminalDeviceMerchantOrganizationUuid
            );
        },
        enabled: effectiveTerminalDeviceMerchantOrganizationUuid !== undefined,
    });

    useEffect(() => {
        if (detailQuery.data === undefined || isEditing) {
            return;
        }
        setDraftConfiguration(detailQuery.data.terminal_device_configuration);
        setDraftSurchargePctText(
            toEditableNumberInputValue(detailQuery.data.terminal_device_configuration.surcharge_pct)
        );
        setDraftConvenienceFeeText(
            toEditableNumberInputValue(detailQuery.data.terminal_device_configuration.convenience_fee)
        );
    }, [detailQuery.data, isEditing]);
    useEffect(() => {
        if (detailQuery.data === undefined || isEditingBasicInformation) {
            return;
        }
        setDraftName(detailQuery.data.name ?? '');
        setDraftProcessorDeviceId(detailQuery.data.processor_device_id);
        setDraftIsVirtual(detailQuery.data.is_virtual);
        setDraftOrganizationId(merchantOrganizationQuery.data?.organization_uuid ?? null);
    }, [detailQuery.data, isEditingBasicInformation]);

    const updateMutation = useMutation({
        mutationFn: async () => {
            const surchargePctNumber: number | undefined = toOptionalNumber(draftSurchargePctText);
            const convenienceFeeNumber: number | undefined = toOptionalNumber(draftConvenienceFeeText);
            if (
                (surchargePctNumber !== undefined && !Number.isFinite(surchargePctNumber)) ||
                (convenienceFeeNumber !== undefined && !Number.isFinite(convenienceFeeNumber))
            ) {
                throw new Error('Surcharge % and convenience fee overrides must be valid numbers');
            }
            return await paymentsInfrastructureApi.updateTerminalDevice(terminalDeviceReference, {
                terminal_device_configuration: {
                    ...draftConfiguration,
                    surcharge_pct: surchargePctNumber,
                    convenience_fee: convenienceFeeNumber,
                },
            });
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['paymentsInfrastructure', 'terminalDevice', terminalDeviceReference],
            });
            setIsEditing(false);
        },
    });
    const updateBasicInformationMutation = useMutation({
        mutationFn: async () => {
            return await paymentsInfrastructureApi.updateTerminalDevice(terminalDeviceReference, {
                name: draftName.trim().length > 0 ? draftName.trim() : null,
                processor_device_id: draftProcessorDeviceId.trim(),
                is_virtual: draftIsVirtual,
                organization_uuid: draftOrganizationId,
            });
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['paymentsInfrastructure', 'terminalDevice', terminalDeviceReference],
            });
            setIsEditingBasicInformation(false);
        },
    });
    const deleteMutation = useMutation({
        mutationFn: async () =>
            await paymentsInfrastructureApi.deleteTerminalDevice(terminalDeviceReference),
        onSuccess: () => {
            runInBackground(navigate('/payments-infrastructure/terminal-devices'), (error: unknown) => {
                throw error;
            });
        },
    });
    const undeleteMutation = useMutation({
        mutationFn: async () =>
            await paymentsInfrastructureApi.updateTerminalDevice(terminalDeviceReference, {
                status: Status.ACTIVE,
            }),
        onSuccess: async (updatedTerminalDevice: GetTerminalDeviceResponse) => {
            queryClient.setQueryData(
                ['paymentsInfrastructure', 'terminalDevice', terminalDeviceReference],
                updatedTerminalDevice
            );
            await queryClient.invalidateQueries({
                queryKey: ['paymentsInfrastructure', 'terminalDevices'],
            });
        },
    });
    const updateSecretAssociationMutation = useMutation({
        mutationFn: async (secretIdentity: Pick<SecretListItem, 'credential_uuid'> | null) => {
            return await paymentsInfrastructureApi.updateTerminalDevice(terminalDeviceReference, {
                credential_uuid: secretIdentity?.credential_uuid ?? null,
            });
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['paymentsInfrastructure', 'terminalDevice', terminalDeviceReference],
            });
        },
    });

    if (activeRoleAssignmentState.isLoading || detailQuery.isLoading) {
        return <div>Loading...</div>;
    }
    if (!canView) {
        return <div className="text-red-600">Access denied. Insufficient permissions.</div>;
    }
    if (detailQuery.data === undefined) {
        return <div>Terminal device not found.</div>;
    }

    const terminalDevice: GetTerminalDeviceResponse = detailQuery.data;
    const attachedSecretIdentity: Pick<SecretListItem, 'credential_uuid'> | null =
        terminalDevice.credential_uuid === null
            ? null
            : {
                  credential_uuid: terminalDevice.credential_uuid,
              };
    const requiredCredentialTypeForTerminalDevice: CredentialType = terminalDevice.is_virtual
        ? 'VIRTUAL_TERMINAL'
        : 'PHYSICAL_TERMINAL';
    const editingSurchargePct: number | undefined = toOptionalNumber(draftSurchargePctText);
    const editingConvenienceFee: number | undefined = toOptionalNumber(draftConvenienceFeeText);
    const effectiveDraftSurchargePct: number | undefined =
        isEditing && Number.isFinite(editingSurchargePct ?? Number.NaN)
            ? editingSurchargePct
            : draftConfiguration.surcharge_pct;
    const effectiveDraftConvenienceFee: number | undefined =
        isEditing && Number.isFinite(editingConvenienceFee ?? Number.NaN)
            ? editingConvenienceFee
            : draftConfiguration.convenience_fee;
    const effectiveSurchargePct: number | undefined =
        effectiveDraftSurchargePct ??
        parentLogicalTerminalQuery.data?.logical_terminal_configuration.surcharge_pct ??
        parentMerchantAccountQuery.data?.merchant_account_configuration.surcharge_pct;
    const effectiveConvenienceFee: number | undefined =
        effectiveDraftConvenienceFee ??
        parentLogicalTerminalQuery.data?.logical_terminal_configuration.convenience_fee ??
        parentMerchantAccountQuery.data?.merchant_account_configuration.convenience_fee;
    const effectiveAllowOverrideFee: boolean =
        draftConfiguration.allow_override_fee ??
        parentLogicalTerminalQuery.data?.logical_terminal_configuration.allow_override_fee ??
        parentMerchantAccountQuery.data?.merchant_account_configuration.allow_override_fee ??
        false;

    return (
        <div className="space-y-4">
            <BackButton
                href="/payments-infrastructure/terminal-devices"
                tooltip="Back to Terminal Devices"
            />
            <div className="mb-8 flex items-center gap-3">
                <h1 className="text-3xl font-bold">Terminal Device Details</h1>
                <StatusBadge status={terminalDevice.status} />
            </div>
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Basic Information</CardTitle>
                    {canEdit && !isEditingBasicInformation && (
                        <div className="flex gap-2">
                            {terminalDevice.status !== Status.DELETED ? (
                                <Button size="sm" onClick={() => setIsEditingBasicInformation(true)}>
                                    Edit
                                </Button>
                            ) : null}
                            {terminalDevice.status === Status.DELETED ? (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => undeleteMutation.mutate()}
                                    disabled={undeleteMutation.isPending || deleteMutation.isPending}
                                >
                                    Undelete
                                </Button>
                            ) : (
                                <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => setIsDeleteConfirmationOpen(true)}
                                    disabled={deleteMutation.isPending || undeleteMutation.isPending}
                                >
                                    Delete
                                </Button>
                            )}
                        </div>
                    )}
                    {canEdit && isEditingBasicInformation && (
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                onClick={() => updateBasicInformationMutation.mutate()}
                                disabled={
                                    updateBasicInformationMutation.isPending ||
                                    draftProcessorDeviceId.trim().length === 0
                                }
                            >
                                {updateBasicInformationMutation.isPending ? 'Saving...' : 'Save'}
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                    setDraftName(terminalDevice.name ?? '');
                                    setDraftProcessorDeviceId(terminalDevice.processor_device_id);
                                    setDraftIsVirtual(terminalDevice.is_virtual);
                                    setDraftOrganizationId(
                                        merchantOrganizationQuery.data?.organization_uuid ?? null
                                    );
                                    setIsEditingBasicInformation(false);
                                }}
                                disabled={updateBasicInformationMutation.isPending}
                            >
                                Cancel
                            </Button>
                        </div>
                    )}
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                    <div>
                        <p className="text-sm text-muted-foreground">Merchant Account</p>
                        {parentLogicalTerminalQuery.data === undefined ? (
                            <p className="font-semibold text-muted-foreground">Loading...</p>
                        ) : (
                            <p
                                className="font-semibold text-primary underline cursor-pointer"
                                onClick={() => {
                                    runInBackground(
                                        navigate(
                                            `/payments-infrastructure/merchant-accounts/${parentLogicalTerminalQuery.data.merchant_account_uuid}`
                                        ),
                                        (error: unknown) => {
                                            throw error;
                                        }
                                    );
                                }}
                            >
                                {`${terminalDevice.processor_tid} - ${terminalDevice.processor_name}`}
                            </p>
                        )}
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Merchant</p>
                        {isEditingBasicInformation ? (
                            effectiveTerminalDeviceMerchantOrganizationUuid === undefined ? (
                                <p className="font-semibold text-muted-foreground">Loading...</p>
                            ) : (
                                <div className="space-y-2">
                                    <OmnisearchSelect<MerchantOrganizationListItem, string>
                                        label="Merchant"
                                        placeholder="Search merchants..."
                                        pageSize={50}
                                        selectedItemId={draftOrganizationId}
                                        onClearSelection={() => setDraftOrganizationId(null)}
                                        fetchPage={async (params) => {
                                            const response =
                                                await paymentsInfrastructureApi.searchMerchantOrganizations(
                                                    {
                                                        root_organization_uuid:
                                                            parentMerchantAccountQuery.data
                                                                ?.organization_uuid ??
                                                            '',
                                                        ...(params.terms !== undefined
                                                            ? { terms: [...params.terms] }
                                                            : {}),
                                                        limit: params.limit,
                                                        cursor: params.cursor,
                                                    }
                                                );
                                            return {
                                                items: response.merchant_organizations,
                                                pagination: response.pagination,
                                            };
                                        }}
                                        getItemId={(item) => item.organization_uuid}
                                        getItemLabel={(item) => item.name}
                                        getItemFilterText={(item) =>
                                            `${item.name} ${item.organization_uuid}`.toLowerCase()
                                        }
                                        renderItemCard={renderMerchantOrganizationOmnisearchCard}
                                        onSelect={(item) =>
                                            setDraftOrganizationId(item.organization_uuid)
                                        }
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setDraftOrganizationId(null)}
                                    >
                                        Use inherited merchant
                                    </Button>
                                </div>
                            )
                        ) : merchantOrganizationQuery.data === undefined ? (
                            <p className="font-semibold text-muted-foreground">Loading...</p>
                        ) : (
                            <p
                                className="font-semibold text-primary underline cursor-pointer"
                                onClick={() => {
                                    runInBackground(
                                        navigate(
                                            `/organizations/${merchantOrganizationQuery.data.organization_uuid}`
                                        ),
                                        (error: unknown) => {
                                            throw error;
                                        }
                                    );
                                }}
                            >
                                {merchantOrganizationQuery.data.name}
                            </p>
                        )}
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Processor Name</p>
                        <p className="font-semibold">{terminalDevice.processor_name}</p>
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Processor MID</p>
                        <p className="font-semibold">{terminalDevice.processor_mid}</p>
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Logical Terminal</p>
                        <p
                            className="font-semibold text-primary underline cursor-pointer"
                            onClick={() => {
                                runInBackground(
                                    navigate(
                                        `/payments-infrastructure/logical-terminals/${terminalDevice.logical_terminal_uuid}`
                                    ),
                                    (error: unknown) => {
                                        throw error;
                                    }
                                );
                            }}
                        >
                            {`${terminalDevice.processor_mid} - ${terminalDevice.processor_name}`}
                        </p>
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Processor TID</p>
                        <p className="font-semibold">{terminalDevice.processor_tid}</p>
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Name</p>
                        {isEditingBasicInformation ? (
                            <Input
                                value={draftName}
                                onChange={(event) => setDraftName(event.target.value)}
                            />
                        ) : (
                            <p className="font-semibold">{terminalDevice.name ?? '—'}</p>
                        )}
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Processor Device ID</p>
                        {isEditingBasicInformation ? (
                            <Input
                                value={draftProcessorDeviceId}
                                onChange={(event) => setDraftProcessorDeviceId(event.target.value)}
                            />
                        ) : (
                            <p className="font-semibold">{terminalDevice.processor_device_id}</p>
                        )}
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground">Virtual</p>
                        {isEditingBasicInformation ? (
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="detail_terminal_device_is_virtual"
                                    checked={draftIsVirtual}
                                    onCheckedChange={(checkedValue) =>
                                        setDraftIsVirtual(checkedValue === true)
                                    }
                                />
                                <Label htmlFor="detail_terminal_device_is_virtual">Virtual terminal</Label>
                            </div>
                        ) : (
                            <p className="font-semibold">{terminalDevice.is_virtual ? 'Yes' : 'No'}</p>
                        )}
                    </div>
                    {updateBasicInformationMutation.error !== null && (
                        <p className="text-destructive col-span-2">
                            {String(updateBasicInformationMutation.error)}
                        </p>
                    )}
                </CardContent>
            </Card>
            <ConfirmationModal
                open={isDeleteConfirmationOpen}
                onOpenChange={setIsDeleteConfirmationOpen}
                message="Delete this terminal device? This operation marks the status as DELETED."
                onConfirm={() => {
                    deleteMutation.mutate();
                }}
                onCancel={() => {
                    setIsDeleteConfirmationOpen(false);
                }}
                confirmLabel="Delete"
                isLoading={deleteMutation.isPending}
            />

            <ConfigurationEditorShell
                title="Configuration"
                canEdit={canEdit}
                isEditing={isEditing}
                onEdit={() => setIsEditing(true)}
                onCancel={() => {
                    setDraftConfiguration(terminalDevice.terminal_device_configuration);
                    setDraftSurchargePctText(
                        toEditableNumberInputValue(
                            terminalDevice.terminal_device_configuration.surcharge_pct
                        )
                    );
                    setDraftConvenienceFeeText(
                        toEditableNumberInputValue(
                            terminalDevice.terminal_device_configuration.convenience_fee
                        )
                    );
                    setIsEditing(false);
                }}
                onSave={() => updateMutation.mutate()}
                isSaving={updateMutation.isPending}
            >
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="detail_terminal_device_surcharge_pct">
                                Surcharge % override
                            </Label>
                            <Input
                                id="detail_terminal_device_surcharge_pct"
                                disabled={!isEditing}
                                value={draftSurchargePctText}
                                onChange={(event) => setDraftSurchargePctText(event.target.value)}
                            />
                        </div>
                        <div>
                            <Label htmlFor="detail_terminal_device_convenience_fee">
                                Convenience fee override
                            </Label>
                            <Input
                                id="detail_terminal_device_convenience_fee"
                                disabled={!isEditing}
                                value={draftConvenienceFeeText}
                                onChange={(event) => setDraftConvenienceFeeText(event.target.value)}
                            />
                        </div>
                    </div>
                    <InheritedCheckbox
                        id="detail_terminal_device_allow_override_fee"
                        value={draftConfiguration.allow_override_fee}
                        disabled={!isEditing}
                        onValueChange={(nextValue) =>
                            setDraftConfiguration((currentValue) => ({
                                ...currentValue,
                                allow_override_fee: nextValue,
                            }))
                        }
                        label="Allow fee override"
                    />
                    <div className="border-t pt-4 grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-sm text-muted-foreground">Effective surcharge %</p>
                            <p className="font-semibold">
                                {effectiveSurchargePct === undefined ? 'undefined' : effectiveSurchargePct}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">Effective convenience fee</p>
                            <p className="font-semibold">
                                {effectiveConvenienceFee === undefined
                                    ? 'undefined'
                                    : effectiveConvenienceFee}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">
                                Effective allow fee override
                            </p>
                            <p className="font-semibold">
                                {effectiveAllowOverrideFee ? 'Yes' : 'No'}
                            </p>
                        </div>
                    </div>
                </div>
            </ConfigurationEditorShell>

            {parentMerchantAccountQuery.data !== undefined &&
                merchantOrganizationQuery.data !== undefined &&
                merchantOrganizationQuery.data.organization_uuid !== undefined && (
                <SecretAssociationCard
                    cardTitle="Credentials"
                    entityLabelForDialog="terminal device"
                    organizationUuid={merchantOrganizationQuery.data.organization_uuid}
                    organizationName={`${parentMerchantAccountQuery.data.processor_name} / ${parentMerchantAccountQuery.data.processor_mid}`}
                    requiredCredentialType={requiredCredentialTypeForTerminalDevice}
                    credentialTargetFamily="ipg"
                    credentialSystemSelectLabel="Integrated Payments Gateway"
                    availableCredentialSystemIds={SupportedIpgSystemIds}
                    attachedSecretIdentity={attachedSecretIdentity}
                    canEdit={canEdit}
                    onAttachSecret={(secretIdentity) => {
                        updateSecretAssociationMutation.mutate(secretIdentity);
                    }}
                    onDetachSecret={() => {
                        updateSecretAssociationMutation.mutate(null);
                    }}
                    onAssociationChanged={() =>
                        queryClient.invalidateQueries({
                            queryKey: [
                                'paymentsInfrastructure',
                                'terminalDevice',
                                terminalDeviceReference,
                            ],
                        })
                    }
                    isMutating={updateSecretAssociationMutation.isPending}
                />
            )}
            {updateSecretAssociationMutation.error !== null && (
                <p className="text-destructive">{String(updateSecretAssociationMutation.error)}</p>
            )}

        </div>
    );
}

export function toSecretAssociationIdentity(
    secret: Pick<SecretListItem, 'credential_uuid'>
): Pick<SecretListItem, 'credential_uuid'> {
    return {
        credential_uuid: secret.credential_uuid,
    };
}

export function findAttachedSecretByIdentity(
    availableSecrets: ReadonlyArray<SecretListItem> | undefined,
    attachedSecretIdentity: Pick<SecretListItem, 'credential_uuid'> | null
): SecretListItem | null {
    if (availableSecrets === undefined || attachedSecretIdentity === null) {
        return null;
    }
    return (
        availableSecrets.find(
            (secret: SecretListItem) =>
                secret.credential_uuid === attachedSecretIdentity.credential_uuid
        ) ?? null
    );
}

export function filterAvailableSecretsForAssociation(
    secrets: ReadonlyArray<SecretListItem>
): ReadonlyArray<SecretListItem> {
    return secrets.filter((secret: SecretListItem) => secret.status !== Status.DELETED);
}
